/* eslint-disable react/prop-types */
import { Fragment, useEffect, useMemo, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ShieldAlert,
  RefreshCw,
  AlertTriangle,
  TrendingUp,
  Clock,
  Plane,
  Users as UsersIcon,
  CreditCard,
  Sparkles,
  Search as SearchIcon,
  CheckCircle2,
  XCircle,
  Eye,
  ChevronDown,
  ChevronUp,
  DollarSign,
  Brain,
  Calendar,
  Zap,
  FlaskConical,
  BarChart2,
  X as XIcon,
  Info,
  Receipt,
  Activity,
  Droplets,
  Gauge,
  ArrowUpRight,
  ClipboardList,
  TrendingDown,
  Minus,
  Wallet,
  Scale,
  Loader2,
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { User, FlightBooking } from '@/api/entities';
import FlightDetailsModal from '@/components/flight/FlightDetailsModal';
import { toast } from 'sonner';
import PaymentScheduleDisplay from '@/components/booking/PaymentScheduleDisplay';
import DecisionHistoryDialog from '@/components/portfolio/DecisionHistoryDialog';
import { useIsMobile } from '@/hooks/use-mobile';
import { format } from 'date-fns';
import VipBadge from '@/components/admin/VipBadge';
import {
  PAYMENT_ISSUE_COPY,
  SORT_LABELS,
  aiTripFeatures,
  baseNow,
  bookedBase,
  customerName,
  daysUntil,
  fmtNetProfit,
  fmtPct,
  groupTrips,
  lastPayment,
  netProfit,
  netProfitLines,
  paidPct,
  parseDisplayDate,
  passesPaidFilter,
  paymentIssue,
  shortDate,
  sortTrips,
  tripRef,
  tripTotal,
} from './RiskReview.tripTable';

// ─── Design tokens ────────────────────────────────────────────────────────────

const RISK_PILL = {
  critical: 'text-rose-200 bg-rose-950/50 border-rose-400/30',
  high:     'text-orange-200 bg-orange-950/45 border-orange-400/25',
  medium:   'text-brand-100 bg-brand-950/40 border-brand-400/20',
  low:      'text-emerald-100 bg-emerald-950/35 border-emerald-400/20',
};

const RISK_BAR = {
  critical: 'bg-gradient-to-r from-rose-600 to-rose-400',
  high:     'bg-gradient-to-r from-orange-600 to-orange-400',
  medium:   'bg-gradient-to-r from-brand-600 to-brand-400',
  low:      'bg-gradient-to-r from-emerald-700 to-emerald-400',
};

const RISK_TEXT = {
  critical: 'text-rose-300',
  high:     'text-orange-300',
  medium:   'text-brand-200',
  low:      'text-emerald-200',
};

const ACTION_LABEL = {
  BOOK_NOW:              'Book Now',
  WAIT_FOR_NEXT_PAYMENT: 'Wait · Next Pmt',
  HOLD_BOOKING:          'Hold Booking',
  MONITOR:               'Monitor',
};

const ACTION_PILL = {
  BOOK_NOW:              'bg-emerald-950/50 border-emerald-400/30 text-emerald-200',
  WAIT_FOR_NEXT_PAYMENT: 'bg-brand-950/45 border-brand-400/25 text-brand-100',
  HOLD_BOOKING:          'bg-rose-950/45 border-rose-400/30 text-rose-100',
  MONITOR:               'bg-zinc-900/60 border-white/10 text-stone-300',
};

const ACTION_ICON = {
  BOOK_NOW:              CheckCircle2,
  WAIT_FOR_NEXT_PAYMENT: Clock,
  HOLD_BOOKING:          XCircle,
  MONITOR:               Eye,
};

// ─── Portfolio AI action vocabulary ──────────────────────────────────────────
// These come from the Portfolio Intelligence layer (server/portfolio-intelligence.js).
// `portfolio_ai_action` is the portfolio's verdict; `final_action` is the
// synthesised operational recommendation.

const PORTFOLIO_AI_LABEL = {
  APPROVED_TO_BOOK:    'Approved',
  HOLD_FOR_LIQUIDITY:  'Hold · Liquidity',
  PAYMENT_REQUIRED:    'Payment Required',
  MANUAL_REVIEW:       'Manual Review',
};

const PORTFOLIO_AI_PILL = {
  APPROVED_TO_BOOK:    'bg-emerald-950/45 border-emerald-400/25 text-emerald-200',
  HOLD_FOR_LIQUIDITY:  'bg-brand-950/45 border-brand-400/25 text-brand-100',
  PAYMENT_REQUIRED:    'bg-rose-950/45 border-rose-400/25 text-rose-100',
  MANUAL_REVIEW:       'bg-brand-950/45 border-brand-400/25 text-brand-100',
};

const FINAL_ACTION_LABEL = {
  BOOK_NOW:           'Book Now',
  PAYMENT_REQUIRED:   'Payment Required',
  HOLD_FOR_LIQUIDITY: 'Hold · Liquidity',
  WATCH_CLOSELY:      'Watch Closely',
  MANUAL_REVIEW:      'Manual Review',
};

const FINAL_ACTION_PILL = {
  BOOK_NOW:           'bg-emerald-950/55 border-emerald-400/35 text-emerald-100',
  PAYMENT_REQUIRED:   'bg-rose-950/55 border-rose-400/35 text-rose-100',
  HOLD_FOR_LIQUIDITY: 'bg-brand-950/55 border-brand-400/35 text-brand-100',
  WATCH_CLOSELY:      'bg-brand-950/55 border-brand-400/30 text-brand-100',
  MANUAL_REVIEW:      'bg-brand-950/55 border-brand-400/35 text-brand-100',
};

// Liquidity & health colour ladders for the compact PI summary card.
const HEALTH_TONE = (score) => {
  if (score == null) return 'text-stone-400';
  if (score >= 85) return 'text-emerald-300/95';
  if (score >= 70) return 'text-emerald-200/85';
  if (score >= 55) return 'text-brand-200/85';
  if (score >= 40) return 'text-orange-300/85';
  return 'text-rose-300/90';
};

const LCR_TONE = (lcr) => {
  if (lcr == null || !Number.isFinite(lcr)) return 'text-stone-400';
  if (lcr >= 1.5)  return 'text-emerald-300/95';
  if (lcr >= 1.10) return 'text-brand-200/85';
  if (lcr >= 0.85) return 'text-orange-300/85';
  return 'text-rose-300/90';
};

// Full labels for the recommendation panel (no shortening needed there)
const ACTION_LABEL_FULL = {
  BOOK_NOW:              'Book Now',
  WAIT_FOR_NEXT_PAYMENT: 'Wait for Next Payment',
  HOLD_BOOKING:          'Hold Booking',
  MONITOR:               'Monitor',
};

// Fare-trajectory urgency vocabulary surfaced by the deep-dive AI. The model
// reasons about advance-purchase fences, seasonal demand on the specific O&D,
// route competition density, and cabin scarcity for the actual carrier and
// returns one of these urgency tiers along with a recommended ticket-by lead time.
const FARE_URGENCY_PILL = {
  critical: 'bg-rose-950/55 border-rose-400/35 text-rose-100',
  high:     'bg-orange-950/50 border-orange-400/30 text-orange-100',
  medium:   'bg-brand-950/45 border-brand-400/25 text-brand-100',
  low:      'bg-emerald-950/40 border-emerald-400/25 text-emerald-200',
};

const FARE_URGENCY_LABEL = {
  critical: 'Critical fare urgency',
  high:     'High fare urgency',
  medium:   'Moderate fare urgency',
  low:      'Low fare urgency',
};

const VOLATILITY_TONE = {
  extreme:  'text-rose-300/90',
  high:     'text-orange-300/90',
  moderate: 'text-brand-200/85',
  low:      'text-emerald-300/85',
};

const SEASONAL_TONE = {
  extreme_peak: 'text-rose-300/90',
  peak:         'text-orange-300/90',
  shoulder:     'text-brand-200/85',
  trough:       'text-emerald-300/85',
};

const COMPETITION_TONE = {
  monopoly:          'text-rose-300/90',
  duopoly:           'text-orange-300/90',
  competitive:       'text-emerald-300/85',
  hyper_competitive: 'text-emerald-300/95',
};

const SCARCITY_TONE = {
  very_scarce: 'text-rose-300/90',
  scarce:      'text-orange-300/90',
  moderate:    'text-brand-200/85',
  abundant:    'text-emerald-300/85',
};

function fmtTier(v) {
  if (!v) return '—';
  return String(v).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Glass panel */
const PANEL = 'rounded-2xl border border-white/[0.08] bg-zinc-950/60 backdrop-blur-2xl shadow-[0_24px_80px_-40px_rgba(0,0,0,0.85),inset_0_1px_0_0_rgba(255,255,255,0.055)]';
/** Inset panel (inside a PANEL) */
const INSET = 'rounded-xl border border-white/[0.05] bg-gradient-to-br from-white/[0.035] via-transparent to-transparent';
/** Risk-tinted accent strip — replaces the old chunky `border-l-[3px]` left
 *  rail. A thin 2px gradient column with a soft side-glow reads as a modern
 *  status marker instead of an old-school category divider. */
const RISK_RAIL = {
  critical: 'before:bg-gradient-to-b before:from-rose-400/80 before:via-rose-500/60 before:to-rose-700/40 before:shadow-[0_0_18px_-4px_rgba(244,63,94,0.45)]',
  high:     'before:bg-gradient-to-b before:from-orange-400/75 before:via-orange-500/55 before:to-orange-700/35 before:shadow-[0_0_16px_-4px_rgba(249,115,22,0.35)]',
  medium:   'before:bg-gradient-to-b before:from-brand-300/65 before:via-brand-400/50 before:to-brand-600/30 before:shadow-[0_0_14px_-4px_rgba(99,148,247,0.30)]',
  low:      'before:bg-gradient-to-b before:from-emerald-400/65 before:via-emerald-500/50 before:to-emerald-700/30 before:shadow-[0_0_14px_-4px_rgba(52,211,153,0.30)]',
};

/** Severity tier each AI verdict implies — used to colour the rail so the
 *  left edge always agrees with the top-right verdict pill. Without this,
 *  an `HOLD_BOOKING` row whose deterministic `risk_level` is `low` (Risk 22)
 *  would show a calm green rail next to an alarming red pill — a real
 *  signal-disconnect users will notice. */
const AI_VERDICT_TIER = {
  BOOK_NOW:              'low',
  MONITOR:               'medium',
  WAIT_FOR_NEXT_PAYMENT: 'high',
  HOLD_BOOKING:          'critical',
};

/** Severity tier each portfolio final action implies — same idea as
 *  AI_VERDICT_TIER, used when the portfolio layer actually overrules the AI. */
const FINAL_ACTION_TIER = {
  BOOK_NOW:           'low',
  WATCH_CLOSELY:      'medium',
  MANUAL_REVIEW:      'medium',
  HOLD_FOR_LIQUIDITY: 'high',
  PAYMENT_REQUIRED:   'critical',
};

const TIER_ORDER = { low: 0, medium: 1, high: 2, critical: 3 };

/** Returns the most-concerning severity tier across the deterministic risk
 *  level, the AI verdict, and (when it overrules the AI) the portfolio
 *  final action. The rail's job is to summarise "how worried should I be
 *  about this row?" — and that answer must reflect every operational signal
 *  feeding the verdict pill on the right, not just the underlying risk score. */
function effectiveRiskTier({ riskLevel, aiVerdict, finalAction }) {
  const tiers = [riskLevel];
  if (aiVerdict && AI_VERDICT_TIER[aiVerdict])     tiers.push(AI_VERDICT_TIER[aiVerdict]);
  if (finalAction && FINAL_ACTION_TIER[finalAction]) tiers.push(FINAL_ACTION_TIER[finalAction]);
  return tiers
    .filter((t) => t in TIER_ORDER)
    .reduce((max, t) => (TIER_ORDER[t] > TIER_ORDER[max] ? t : max), 'low');
}

/** Primary action pill on each trip — mirrors TripDetailPanel: AI verdict when present,
 *  otherwise the deterministic `recommendation.action`. The action filter must
 *  use this same source or "Book Now" hides rows whose pill says BOOK_NOW but
 *  whose underlying recommendation is MONITOR/WAIT. */
function displayedTripAction(a) {
  const aiOk = a.ai_assessment?.ok !== false && !!a.ai_assessment?.verdict;
  if (aiOk) return a.ai_assessment.verdict;
  return a.recommendation?.action || null;
}

/** Heuristic near-term fare-direction forecast from SerpAPI price insights.
 *  Google's `price_level` is the primary signal — `low` means the route is
 *  currently cheap, so fares tend to RISE from here; `high` means it's
 *  currently expensive, so they tend to FALL. We corroborate with where the
 *  current low sits inside the typical band and the recent price-history
 *  trend. Returns null when there are no insights to reason over. */
function forecastFare(pi) {
  if (!pi) return null;
  const level   = String(pi.price_level || '').toLowerCase();
  const hasLevel = level === 'low' || level === 'typical' || level === 'high';
  const range   = Array.isArray(pi.typical_price_range) ? pi.typical_price_range : null;
  const lowest  = Number(pi.lowest_price);
  const history = Array.isArray(pi.price_history) ? pi.price_history : [];

  // Recent trend: mean of the last 3 points vs the earlier points (fraction).
  let trend = 0;
  const prices = history.map((h) => Number(h?.price)).filter((p) => Number.isFinite(p) && p > 0);
  if (prices.length >= 4) {
    const recent  = prices.slice(-3);
    const earlier = prices.slice(0, -3);
    const avg = (arr) => arr.reduce((s, x) => s + x, 0) / arr.length;
    const e = avg(earlier);
    if (e > 0) trend = (avg(recent) - e) / e;
  }

  // Position of the current low inside the typical band (0 = cheap, 1 = pricey).
  let pos = null;
  if (range && range.length === 2 && range[1] > range[0] && Number.isFinite(lowest)) {
    pos = Math.min(1, Math.max(0, (lowest - range[0]) / (range[1] - range[0])));
  }

  // No usable signal — return null so we don't imply a confident "hold".
  // Mirrors the server's computeFareForecast so the strip and this modal agree.
  if (!hasLevel && pos == null && prices.length < 4) return null;

  let direction;
  if (level === 'low') direction = 'up';
  else if (level === 'high') direction = 'down';
  else {
    // No decisive level signal ("typical" or missing) — lean on band position
    // first, then the recent trend.
    if (pos != null && pos <= 0.33) direction = 'up';
    else if (pos != null && pos >= 0.67) direction = 'down';
    else if (trend >= 0.05) direction = 'up';
    else if (trend <= -0.05) direction = 'down';
    else direction = 'flat';
  }

  const levelCopy =
    level === 'low'     ? 'Fares are currently low for this route'
    : level === 'high'  ? 'Fares are currently high for this route'
    : level === 'typical' ? 'Fares are in their typical range for this route'
    : 'Based on the recent price trend for this route';

  if (direction === 'up') {
    return {
      direction,
      Icon: TrendingUp,
      label: 'Likely to rise',
      tone: { pill: 'bg-orange-950/45 border-orange-400/25 text-orange-100', accent: 'text-orange-300', box: 'bg-orange-950/20 border-orange-400/15' },
      copy: `${levelCopy} — prices are more likely to go up from here than down. Ticketing sooner protects this fare.`,
    };
  }
  if (direction === 'down') {
    return {
      direction,
      Icon: TrendingDown,
      label: 'Likely to drop',
      tone: { pill: 'bg-emerald-950/45 border-emerald-400/25 text-emerald-100', accent: 'text-emerald-300', box: 'bg-emerald-950/20 border-emerald-400/15' },
      copy: `${levelCopy} — prices are more likely to come down from here. Waiting could secure a lower fare, unless default risk argues for locking in now.`,
    };
  }
  return {
    direction,
    Icon: Minus,
    label: 'Likely to hold',
    tone: { pill: 'bg-stone-900/60 border-white/10 text-stone-200', accent: 'text-stone-300', box: 'bg-zinc-900/40 border-white/[0.06]' },
    copy: `${levelCopy} — no strong pressure up or down expected in the near term.`,
  };
}

/** Amber micro-label */
const MICRO = 'text-xs uppercase tracking-[0.2em] text-brand-200/35 font-medium';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const BEHAVIOR_LABEL = {
  impulsive:     { label: 'Impulsive',     color: 'text-rose-300/80',    bg: 'bg-rose-950/35 border-rose-400/20' },
  fast:          { label: 'Fast buyer',    color: 'text-orange-300/80',  bg: 'bg-orange-950/35 border-orange-400/20' },
  deliberate:    { label: 'Deliberate',    color: 'text-emerald-300/80', bg: 'bg-emerald-950/35 border-emerald-400/20' },
  hesitant:      { label: 'Hesitant',      color: 'text-brand-200/85',   bg: 'bg-brand-950/35 border-brand-400/20' },
  very_hesitant: { label: 'Very hesitant', color: 'text-rose-200/80',    bg: 'bg-rose-950/35 border-rose-400/20' },
  unknown:       { label: 'No data',       color: 'text-stone-500',      bg: 'bg-zinc-900/50 border-white/[0.06]' },
};

function fmtHours(h) {
  if (h == null) return '—';
  if (h < 1)   return `${Math.round(h * 60)}m`;
  if (h < 24)  return `${Math.round(h)}h`;
  return `${Math.round(h / 24)}d`;
}

/** Returns Tailwind classes for a Portfolio-Intelligence booking-priority pill. */
function priorityPillFor(score) {
  if (score >= 75) return 'text-emerald-200/95 bg-emerald-950/45 border-emerald-400/25';
  if (score >= 60) return 'text-brand-200/90 bg-brand-950/40 border-brand-400/20';
  if (score >= 45) return 'text-brand-200/85 bg-brand-950/40 border-brand-400/20';
  return 'text-stone-400 bg-zinc-900/55 border-white/10';
}

/** Returns Tailwind classes for a Stripe Radar score badge */
function radarBadgeStyle(score, level) {
  const lvl = level || '';
  if (lvl === 'highest'  || score >= 75) return 'text-rose-200/90 bg-rose-950/45 border-rose-400/25';
  if (lvl === 'elevated' || score >= 55) return 'text-orange-200/85 bg-orange-950/40 border-orange-400/20';
  if (score != null && score >= 35)       return 'text-brand-200/80 bg-brand-950/40 border-brand-400/20';
  if (score != null && score <= 25)       return 'text-emerald-200/80 bg-emerald-950/45 border-emerald-400/20';
  return 'text-stone-400 bg-zinc-900/50 border-white/10';
}

function fmt$(n) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', maximumFractionDigits: 0,
  }).format(Number(n || 0));
}

function fmtDate(ts) {
  if (!ts) return '—';
  // Format: "Thu, Jun 27 2026" — weekday prefix makes scanning much faster
  // when admins are eyeballing payment schedules and travel dates.
  try { return format(new Date(ts), 'EEE, MMM d yyyy'); }
  catch { return '—'; }
}

function daysLabel(d) {
  if (d == null) return '—';
  if (d < 0) return `${Math.abs(d)}d ago`;
  if (d === 0) return 'Today';
  return `in ${d}d`;
}

function hasUsableAiPortfolioSummary(ai) {
  if (!ai || typeof ai !== 'object') return false;
  if (ai.error && typeof ai.error === 'string') return false;
  return Boolean(
    ai.portfolio_summary ||
      (Array.isArray(ai.key_patterns) && ai.key_patterns.length > 0) ||
      ai.booking_timing_advice ||
      ai.demographic_signal ||
      (Array.isArray(ai.immediate_actions) && ai.immediate_actions.length > 0),
  );
}

/** Mirrors server `buildRiskReviewTopRisksForAiSummary`. */
function buildRiskReviewTopRisksClientPayload(assessments) {
  return (assessments || []).slice(0, 25).map((a) => ({
    trip_reference: a.trip_reference || a.booking_id,
    route: a.route,
    airline: a.marketing_airline,
    cabin: a.cabin_class,
    international: a.international,
    payment_method: a.payment_method,
    passengers: (a.passengers_detail || []).map((p) => ({
      age: p.age,
      gender: p.gender,
      nationality: p.nationality,
    })),
    days_until_departure: a.days_until_departure,
    total_amount: a.total_amount,
    paid_percentage: a.paid_percentage,
    paid_data_source: a.paid_data_source ?? 'live',
    deposit_percentage: a.deposit_percentage,
    remaining_amount: a.remaining_amount,
    payments_made: a.payment_history?.length ?? null,
    subscription_status: a.subscription_status,
    last_payment_failed: a.last_payment_failed,
    deadline_date: a.deadline_date,
    next_payment_date: a.next_payment_date,
    risk_score: a.risk_score,
    risk_level: a.risk_level,
    risk_drivers: a.risk_drivers,
    recommendation: a.recommendation,
    source: a.source,
    stripe_radar_score: a.stripe_radar_score ?? null,
    stripe_radar_level: a.stripe_radar_level ?? null,
    user_prior_completed:       a.user_prior_completed ?? null,
    user_prior_cancelled:       a.user_prior_cancelled ?? null,
    user_prior_defaulted:       a.user_prior_defaulted ?? null,
    user_booking_behavior:      a.user_booking_behavior ?? null,
    user_avg_conversion_hours:  a.user_avg_conversion_hours ?? null,
    user_abandoned_cart_count: a.user_abandoned_cart_count ?? null,
    user_is_first_time:         a.user_is_first_time ?? null,
  }));
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function RiskReview() {
  const [currentUser, setCurrentUser] = useState(null);
  const [isUserLoading, setIsUserLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [assessments, setAssessments] = useState([]);
  const [stats, setStats] = useState(null);
  const [aiSummary, setAiSummary] = useState(null);
  const [portfolioSummary, setPortfolioSummary] = useState(null);
  const [empiricalAtlas, setEmpiricalAtlas] = useState(null);
  const [error, setError] = useState(null);
  const [aiNarrativeLoading, setAiNarrativeLoading] = useState(false);
  const aiSeqRef = useRef(0);
  // Per-trip AI batch (runs on every Refresh+AI for ALL unticketed trips so the
  // list view shows a holistic customer+payment+fare verdict on each row, not
  // just inside the deep-dive modal). Progress is shown in the header.
  const [aiPerTripLoading, setAiPerTripLoading] = useState(false);
  const [aiPerTripProgress, setAiPerTripProgress] = useState({ done: 0, total: 0, errors: 0 });
  const aiPerTripSeqRef = useRef(0);

  // Brief "all complete" flash for the processing status panel once every phase
  // (data fetch, per-trip AI, portfolio summary) settles.
  const [statusComplete, setStatusComplete] = useState(false);
  const statusFlashRef = useRef(false);
  // Timestamp of the last fully-completed processing run. Seeded from the
  // server snapshot on mount (cross-device truth), bumped locally when a
  // commanded run settles.
  const [lastCompletedAt, setLastCompletedAt] = useState(null);

  // ?focus=<bookingId> deep-link from Portfolio Intelligence — auto-scroll +
  // briefly highlight that row.
  const focusedBookingId = useMemo(() => {
    try {
      return new URLSearchParams(window.location.search).get('focus');
    } catch { return null; }
  }, []);

  const [riskFilter, setRiskFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');
  const [paymentTypeFilter, setPaymentTypeFilter] = useState('all');
  const [sortBy, setSortBy] = useState('departure');
  const [searchTerm, setSearchTerm] = useState('');
  const [paidFilter, setPaidFilter] = useState('all');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const activeFilterCount = [riskFilter, actionFilter, paymentTypeFilter, paidFilter].filter((v) => v !== 'all').length;
  // The trip table leads the page; the portfolio panels fold away beneath one
  // bar (founder ask, 2026-10-02). Open/closed is a per-viewer convenience.
  const [showAnalytics, setShowAnalytics] = useState(() => {
    try { return window.localStorage.getItem('riskReview.showAnalytics') === '1'; }
    catch { return false; }
  });
  const toggleAnalytics = () => {
    setShowAnalytics((open) => {
      try { window.localStorage.setItem('riskReview.showAnalytics', open ? '0' : '1'); }
      catch { /* storage unavailable — still toggles for this visit */ }
      return !open;
    });
  };

  const [deepDiveTrip, setDeepDiveTrip] = useState(null);
  const [deepDiveData, setDeepDiveData] = useState(null);
  const [deepDiveLoading, setDeepDiveLoading] = useState(false);

  const [showAi, setShowAi] = useState(true);
  const [showBreakdowns, setShowBreakdowns] = useState(false);
  const [calibrateData, setCalibrateData] = useState(null);
  const [calibrateLoading, setCalibrateLoading] = useState(false);
  const [showCalibrate, setShowCalibrate] = useState(false);

  const canAccess =
    !!currentUser &&
    (currentUser.role === 'admin' || currentUser.app_metadata?.roles?.includes('admin'));

  // Bump CACHE_VERSION whenever the assessment payload shape changes on the
  // server so an older stored snapshot (without new fields) is discarded.
  const CACHE_VERSION = 'v17_committed_credit';

  // The analysis lives in ONE server-persisted snapshot (migration 132,
  // GET/PUT /api/admin/risk-review/snapshot) — cross-device, survives
  // sign-out, and NEVER regenerates on its own: mount renders whatever is
  // stored; only the Refresh buttons run the analysis (founder call,
  // 2026-08-04). localStorage is out of the loop entirely.
  //
  // snapshotRef mirrors the currently-rendered blob; the run writes it and
  // each AI phase merges into it. Saves are debounced because per-trip AI
  // completes as several concurrent batches, each of which queues a save.
  const snapshotRef = useRef(null); // { data, generated_at (ms) }
  const [snapshotGeneratedAt, setSnapshotGeneratedAt] = useState(null);
  const snapshotSaveTimerRef = useRef(null);
  // Unmount kill-switch (audit F1, 2026-08-04): without it, a navigated-away
  // instance's still-running AI batches kept merging into their private
  // snapshotRef and PUTting a stale run over whatever a fresh instance had
  // since generated. Checked in putSnapshot; the cleanup below also bumps
  // both AI sequence refs so in-flight batch loops abort at their next guard.
  const instanceAliveRef = useRef(true);
  // One save-failure toast per run — the debounced saves would otherwise
  // spam one toast per completed batch (audit F2).
  const saveFailureToastShownRef = useRef(false);

  const putSnapshot = async () => {
    if (!instanceAliveRef.current) return;
    const snap = snapshotRef.current;
    if (!snap?.data) return;
    try {
      const { supabase } = await import('@/lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;
      const resp = await fetch('/api/admin/risk-review/snapshot', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({
          payload: snap.data,
          cache_version: CACHE_VERSION,
          generated_at: new Date(snap.generated_at).toISOString(),
        }),
      });
      if (!resp.ok && !saveFailureToastShownRef.current) {
        // The whole point of the snapshot is "every device sees this run" —
        // a silent save failure breaks that promise undetectably (audit F2).
        saveFailureToastShownRef.current = true;
        toast.error('Could not save this review to the server — other devices may not see it. Re-run Refresh to retry.');
      }
    } catch {
      if (!saveFailureToastShownRef.current) {
        saveFailureToastShownRef.current = true;
        toast.error('Could not save this review to the server — other devices may not see it. Re-run Refresh to retry.');
      }
    }
  };

  const queueSnapshotSave = () => {
    if (!instanceAliveRef.current) return;
    if (snapshotSaveTimerRef.current) clearTimeout(snapshotSaveTimerRef.current);
    snapshotSaveTimerRef.current = setTimeout(() => { void putSnapshot(); }, 1500);
  };

  useEffect(() => {
    instanceAliveRef.current = true;
    return () => {
      instanceAliveRef.current = false;
      if (snapshotSaveTimerRef.current) clearTimeout(snapshotSaveTimerRef.current);
      // Abort in-flight AI batch loops at their next sequence-guard check.
      aiSeqRef.current += 1;
      aiPerTripSeqRef.current += 1;
    };
  }, []);

  const applyData = (data) => {
    setAssessments(Array.isArray(data.assessments) ? data.assessments : []);
    setStats(data.stats || null);
    setAiSummary(data.ai_summary || null);
    setPortfolioSummary(data.portfolio_summary || null);
    setEmpiricalAtlas(data.empirical_atlas || null);
  };

  useEffect(() => {
    (async () => {
      try { setCurrentUser(await User.me()); }
      catch { /* ignore */ }
      finally { setIsUserLoading(false); }
    })();
  }, []);

  // Strip the assessment down to only the fields the AI helper needs. Keeps
  // POST payloads under ~4-6 KB per trip even with passengers and payment
  // history attached on the row. aiTripFeatures drops the previous
  // ai_assessment, so a prior AI run never feeds back into the model on a
  // re-run, and the display-only net profit inputs.
  const buildAiBatchPayloadForTrip = (a) => {
    const featuresOnly = aiTripFeatures(a);
    return {
      booking_id: a.booking_id,
      source: a.source,
      deterministic_score: a.risk_score,
      deterministic_drivers: a.risk_drivers || [],
      deterministic_recommendation: a.recommendation || null,
      // Pass the assessment-as-features. extractRiskFeaturesFromBooking spreads
      // all feature fields into the top-level assessment, so this is a faithful
      // feature object minus a handful of UI-only additions the AI ignores.
      features: featuresOnly,
    };
  };

  // Persist AI results back into the server snapshot so every device sees
  // them until the next commanded run. Optionally also merges a map of
  // booking_id → live_fare so the stored row carries the current SerpAPI
  // price delta alongside the AI verdict.
  const mergeAiAssessmentsIntoSnapshot = (resultsByBookingId, liveFareByBookingId = null) => {
    const snap = snapshotRef.current;
    if (!snap?.data?.assessments) return;
    snap.data.assessments = snap.data.assessments.map((a) => {
      const next = resultsByBookingId.get(a.booking_id);
      const lf = liveFareByBookingId ? liveFareByBookingId.get(a.booking_id) : null;
      if (!next && !lf) return a;
      const merged = { ...a };
      if (next) merged.ai_assessment = next;
      if (lf)   merged.live_fare     = lf;
      return merged;
    });
    queueSnapshotSave();
  };

  // Holistic per-trip AI: customer + payment posture + route/airline fare
  // trajectory all in one verdict per row. Runs after Refresh+AI for every
  // unticketed trip. Chunked + parallel so a 50-trip portfolio finishes in
  // roughly the time it takes to do one batch.
  const fetchAiPerTripAssessments = async (assessmentsList) => {
    if (!Array.isArray(assessmentsList) || assessmentsList.length === 0) return;
    const id = ++aiPerTripSeqRef.current;
    const BATCH_SIZE   = 8;   // must stay <= server RISK_REVIEW_AI_BATCH_MAX_TRIPS
    const CONCURRENCY  = 3;   // simultaneous batch requests (each request fans out server-side too)

    const all = [...assessmentsList];
    const batches = [];
    for (let i = 0; i < all.length; i += BATCH_SIZE) batches.push(all.slice(i, i + BATCH_SIZE));

    setAiPerTripLoading(true);
    setAiPerTripProgress({ done: 0, total: all.length, errors: 0 });
    let doneCount = 0;
    let errCount  = 0;

    try {
      const { supabase } = await import('@/lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        setAiPerTripLoading(false);
        return;
      }
      const authHeader = `Bearer ${session.access_token}`;

      let cursor = 0;
      const runBatch = async () => {
        while (true) {
          if (id !== aiPerTripSeqRef.current) return; // stale, abort silently
          const batchIdx = cursor++;
          if (batchIdx >= batches.length) return;
          const chunk = batches[batchIdx];
          try {
            const resp = await fetch('/api/admin/risk-review/ai-per-trip-batch', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: authHeader },
              body: JSON.stringify({ trips: chunk.map(buildAiBatchPayloadForTrip) }),
            });
            if (!resp.ok) throw new Error((await resp.text()) || `HTTP ${resp.status}`);
            const json = await resp.json();
            if (id !== aiPerTripSeqRef.current) return;
            const generatedAt = json.generated_at || new Date().toISOString();
            const model       = json.model || null;
            const byId = new Map();
            const liveFareById = new Map();
            for (const r of (json.results || [])) {
              if (!r?.booking_id) continue;
              if (r.live_fare) liveFareById.set(r.booking_id, r.live_fare);
              if (r.ok && r.analysis) {
                byId.set(r.booking_id, {
                  ok: true,
                  model,
                  generated_at: generatedAt,
                  ...r.analysis,
                });
              } else {
                errCount++;
                byId.set(r.booking_id, {
                  ok: false,
                  model,
                  generated_at: generatedAt,
                  error: r?.error || 'AI failed',
                });
              }
            }
            // Stream this batch into state and persist through to cache. The
            // live-fare delta is stashed on the assessment too so a future UI
            // surface can show "+18% above booked" on each row without
            // re-fetching from SerpAPI.
            setAssessments((prev) => prev.map((a) => {
              const merged = byId.get(a.booking_id);
              if (!merged) return a;
              const lf = liveFareById.get(a.booking_id);
              return lf
                ? { ...a, ai_assessment: merged, live_fare: lf }
                : { ...a, ai_assessment: merged };
            }));
            mergeAiAssessmentsIntoSnapshot(byId, liveFareById);
            doneCount += chunk.length;
            setAiPerTripProgress({ done: doneCount, total: all.length, errors: errCount });
          } catch (e) {
            if (id !== aiPerTripSeqRef.current) return;
            errCount += chunk.length;
            doneCount += chunk.length;
            setAiPerTripProgress({ done: doneCount, total: all.length, errors: errCount });
            // Mark this chunk's trips with an error so the row UI can show a
            // muted "AI unavailable" badge rather than leaving them blank.
            const errBlob = { ok: false, error: e instanceof Error ? e.message : 'AI batch failed', generated_at: new Date().toISOString() };
            const byId = new Map(chunk.map((a) => [a.booking_id, errBlob]));
            setAssessments((prev) => prev.map((a) => {
              const merged = byId.get(a.booking_id);
              return merged ? { ...a, ai_assessment: merged } : a;
            }));
            mergeAiAssessmentsIntoSnapshot(byId);
          }
        }
      };

      await Promise.all(Array.from({ length: Math.min(CONCURRENCY, batches.length) }, () => runBatch()));
    } finally {
      if (id === aiPerTripSeqRef.current) setAiPerTripLoading(false);
    }
  };

  const fetchAiPortfolioNarrative = async (statsVal, assessmentsList) => {
    if (!statsVal || !Array.isArray(assessmentsList) || assessmentsList.length === 0) return;
    const id = ++aiSeqRef.current;
    setAiNarrativeLoading(true);
    try {
      const { supabase } = await import('@/lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;
      const top_risks = buildRiskReviewTopRisksClientPayload(assessmentsList);
      const resp = await fetch('/api/admin/risk-review/ai-summary', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ stats: statsVal, top_risks }),
      });
      if (!resp.ok) throw new Error((await resp.text()) || `HTTP ${resp.status}`);
      const json = await resp.json();
      if (id !== aiSeqRef.current) return;
      const nextAi = json.ai_summary ?? null;
      setAiSummary(nextAi);
      if (snapshotRef.current?.data) { snapshotRef.current.data.ai_summary = nextAi; queueSnapshotSave(); }
    } catch (e) {
      if (id !== aiSeqRef.current) return;
      const msg = e instanceof Error ? e.message : 'AI narrative failed';
      const errBlob = { error: msg };
      setAiSummary(errBlob);
      if (snapshotRef.current?.data) { snapshotRef.current.data.ai_summary = errBlob; queueSnapshotSave(); }
      toast.error('AI portfolio narrative failed');
    } finally {
      if (id === aiSeqRef.current) setAiNarrativeLoading(false);
    }
  };

  const load = async (forceRefresh = false, opts = {}) => {
    const { dataOnly = false } = opts;
    if (!canAccess) return;

    // Soft load (mount / revisit): render the stored server snapshot and
    // STOP. No scoring run, no AI calls — the review regenerates strictly
    // on command. A missing snapshot (or one from an older payload shape)
    // just renders the empty state with the Refresh CTA.
    if (!forceRefresh) {
      try {
        if (assessments.length === 0) setIsLoading(true);
        const { supabase } = await import('@/lib/supabase');
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) return;
        const resp = await fetch('/api/admin/risk-review/snapshot', {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        const json = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          // A FAILED fetch must not masquerade as "no stored review yet" —
          // that CTA invites an unnecessary (billed) regeneration (audit F2).
          setError('Could not load the stored review — refresh to try again.');
          return;
        }
        const snap = json?.snapshot;
        if (snap?.payload && snap.cache_version === CACHE_VERSION) {
          const genMs = Date.parse(snap.generated_at) || Date.now();
          snapshotRef.current = { data: snap.payload, generated_at: genMs };
          setSnapshotGeneratedAt(genMs);
          setLastCompletedAt((prev) => prev || Date.parse(snap.updated_at) || genMs);
          applyData(snap.payload);
        }
      } catch {
        setError('Could not load the stored review — refresh to try again.');
      }
      finally { setIsLoading(false); }
      return;
    }

    try {
      if (assessments.length === 0) setIsLoading(true);
      setIsRefreshing(true);
      setError(null);
      const { supabase } = await import('@/lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Not authenticated');
      const resp = await fetch('/api/admin/risk-review', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ include_ai: false }),
      });
      if (!resp.ok) throw new Error((await resp.text()) || `HTTP ${resp.status}`);
      const data = await resp.json();
      applyData(data);
      // The commanded run is the moment the snapshot's clock starts; AI
      // phases merge into this same run via snapshotRef + queueSnapshotSave.
      const genMs = Date.now();
      snapshotRef.current = { data, generated_at: genMs };
      setSnapshotGeneratedAt(genMs);
      void putSnapshot();
      if (!dataOnly && data.stats && Array.isArray(data.assessments) && data.assessments.length > 0) {
        void fetchAiPortfolioNarrative(data.stats, data.assessments);
        void fetchAiPerTripAssessments(data.assessments);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
      toast.error('Failed to load risk review');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    if (canAccess) load(false, {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAccess]);

  // After assessments load, if there's a ?focus= deep-link, scroll the matching
  // row into view (it also opens expanded) and tint it briefly. Once per
  // visit: AI batches re-set `assessments` as they stream in, and each one
  // used to yank the page back to this row mid-read.
  const focusDoneRef = useRef(false);
  useEffect(() => {
    if (!focusedBookingId || assessments.length === 0 || focusDoneRef.current) return;
    // Defer to next frame so the row is mounted.
    requestAnimationFrame(() => {
      const el = document.querySelector(`[data-booking-id="${focusedBookingId}"]`);
      if (el) {
        focusDoneRef.current = true;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('bg-brand-400/[0.14]');
        setTimeout(() => el.classList.remove('bg-brand-400/[0.14]'), 2400);
      }
    });
  }, [focusedBookingId, assessments]);

  // When all processing phases settle (data + per-trip AI + portfolio summary),
  // flash an "all complete" state on the status panel for a few seconds so the
  // admin gets a clear "done" signal instead of guessing.
  useEffect(() => {
    const active = isRefreshing || aiNarrativeLoading || aiPerTripLoading;
    if (statusFlashRef.current && !active) {
      setStatusComplete(true);
      const now = Date.now();
      setLastCompletedAt(now);
      // (Freshness now lives on the server snapshot row — nothing local to stamp.)
      const t = setTimeout(() => setStatusComplete(false), 6000);
      statusFlashRef.current = active;
      return () => clearTimeout(t);
    }
    statusFlashRef.current = active;
  }, [isRefreshing, aiNarrativeLoading, aiPerTripLoading]);

  const openDeepDive = async (trip) => {
    setDeepDiveTrip(trip);
    // If we already have a successful per-trip AI assessment from the bulk
    // Refresh+AI pass, render that immediately. The modal can still call out
    // to /deep-dive in the background to get the higher-rigor gpt-4o pass,
    // but the admin sees something useful straight away.
    const cachedAi = trip?.ai_assessment && trip.ai_assessment.ok !== false ? trip.ai_assessment : null;
    if (cachedAi) {
      setDeepDiveData({
        success: true,
        cached_from_bulk: true,
        features: trip,
        deterministic: { score: trip.risk_score, drivers: trip.risk_drivers || [], recommendation: trip.recommendation },
        user_history: {
          prior_cancellations:   trip.user_prior_cancelled      ?? 0,
          prior_completed:       trip.user_prior_completed      ?? 0,
          prior_defaulted:       trip.user_prior_defaulted      ?? 0,
          total_prior_bookings:  trip.user_total_prior_bookings  ?? 0,
          abandoned_cart_count:  trip.user_abandoned_cart_count  ?? 0,
          avg_conversion_hours:  trip.user_avg_conversion_hours  ?? null,
          booking_behavior:      trip.user_booking_behavior      ?? null,
          cart_abandon_rate:     trip.user_cart_abandon_rate     ?? null,
          is_first_time:         trip.user_is_first_time         ?? null,
        },
        analysis: cachedAi,
      });
      setDeepDiveLoading(false);
      return;
    }
    setDeepDiveData(null);
    setDeepDiveLoading(true);
    try {
      const { supabase } = await import('@/lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Not authenticated');
      const resp = await fetch('/api/admin/risk-review/deep-dive', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ booking_id: trip.booking_id, source: trip.source }),
      });
      if (!resp.ok) throw new Error((await resp.text()) || `HTTP ${resp.status}`);
      setDeepDiveData(await resp.json());
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Deep dive failed');
      setDeepDiveData({ error: e instanceof Error ? e.message : 'failed' });
    } finally {
      setDeepDiveLoading(false);
    }
  };

  const runCalibrate = async () => {
    setCalibrateLoading(true);
    try {
      const { supabase } = await import('@/lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Not authenticated');
      const resp = await fetch('/api/admin/risk-review/calibrate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({}),
      });
      if (!resp.ok) throw new Error(await resp.text());
      setCalibrateData(await resp.json());
      setShowCalibrate(true);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Calibration failed');
    } finally {
      setCalibrateLoading(false);
    }
  };

  /**
   * "If we booked every unticketed trip right now, are we up or down?"
   *
   * The whole premise of this page is holding a trip back to see where the fare
   * goes, so the scoreboard for that bet belongs on it. Each row's `live_fare`
   * already carries both halves, as PARTY totals for the same itinerary:
   *   booked_base_cents   — the fare the customer's plan was built on
   *   current_price_cents — what that same trip costs today
   * Netting them across the book answers whether the wait is paying.
   *
   * Only rows that carry a live fare count. A trip with no price check is
   * silently absent rather than assumed flat — `priced` vs `total` is rendered
   * on the card so a thin sample can never read as the whole portfolio.
   */
  const farePosition = useMemo(() => {
    const priced = assessments.filter(
      (a) => a.live_fare?.current_price_cents > 0 && a.live_fare?.booked_base_cents > 0,
    );
    let committedCents = 0, todayCents = 0, cushionCents = 0, exactMatches = 0;
    const cheaper = [], pricier = [];
    let flatCount = 0;

    for (const a of priced) {
      const lf = a.live_fare;
      committedCents += lf.booked_base_cents;
      todayCents += lf.current_price_cents;
      // Service + hold ride on top of the base. The hold fee exists precisely to
      // absorb a fare that moves up, so a deficit is only a real loss once it
      // chews past this.
      const grand = lf.booked_grand_total_cents
        || Math.round((Number(a.grand_total_with_hold) || 0) * 100);
      cushionCents += Math.max(0, grand - lf.booked_base_cents);
      if (lf.matched_exact_flight) exactMatches += 1;

      const delta = lf.booked_base_cents - lf.current_price_cents; // + = costs less than planned
      if (delta > 0) cheaper.push({ ...a, delta });
      else if (delta < 0) pricier.push({ ...a, delta });
      else flatCount += 1;
    }

    const netCents = committedCents - todayCents;
    return {
      pricedCount: priced.length,
      totalCount: assessments.length,
      exactMatches,
      committedCents,
      todayCents,
      netCents,
      cushionCents,
      afterCushionCents: netCents + cushionCents,
      pctOfCommitted: committedCents > 0 ? (netCents / committedCents) * 100 : 0,
      cheaperCount: cheaper.length,
      pricierCount: pricier.length,
      flatCount,
      gainCents: cheaper.reduce((s, r) => s + r.delta, 0),
      lossCents: pricier.reduce((s, r) => s + r.delta, 0),
      topSavers: [...cheaper].sort((a, b) => b.delta - a.delta).slice(0, 3),
      topBleeders: [...pricier].sort((a, b) => a.delta - b.delta).slice(0, 3),
    };
  }, [assessments]);

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    const rows = assessments.filter((a) => {
      if (riskFilter !== 'all' && a.risk_level !== riskFilter) return false;
      if (actionFilter !== 'all' && displayedTripAction(a) !== actionFilter) return false;
      if (paymentTypeFilter !== 'all' && a.payment_method !== paymentTypeFilter) return false;
      if (!passesPaidFilter(a, paidFilter)) return false;
      if (!q) return true;
      return [
        a.route, a.marketing_airline, a.user_email, a.user_full_name,
        a.trip_reference, a.booking_reference, a.booking_id, a.payment_method,
        ...(a.passengers_detail || []).map((p) => p.name || ''),
      ].filter(Boolean).join(' ').toLowerCase().includes(q);
    });

    // `departure` keeps the authoritative server order (soonest departure →
    // action urgency → risk); every other key re-orders client-side from the
    // same readers the table cells use, so the order and the visible numbers
    // can never disagree. Departure breaks ties so equal rows still surface
    // the most time-sensitive trip first. `fare_rise` ranks the live-fare
    // forecast (only present after Refresh + AI); trips without one sink.
    return sortTrips(rows, sortBy);
  }, [assessments, riskFilter, actionFilter, paymentTypeFilter, paidFilter, sortBy, searchTerm]);

  // ── Loading / access states ──────────────────────────────────────────────────
  if (isUserLoading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center bg-[#07070a]">
        <div className="flex flex-col items-center gap-3">
          <div className="h-px w-12 bg-gradient-to-r from-transparent via-brand-200/40 to-transparent" />
          <p className="text-stone-500 text-base font-normal tracking-widest">Loading</p>
        </div>
      </div>
    );
  }

  if (!canAccess) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center bg-[#07070a]">
        <p className="text-stone-300 text-base font-normal tracking-wide">Administrator access required.</p>
      </div>
    );
  }

  // Derived breakdown data
  const recDist  = stats?.recommendation_distribution || {};
  const riskDist = stats?.distribution || {};
  const topAirlines = Object.entries(stats?.airline_breakdown || {})
    .sort((a, b) => b[1].avg_risk - a[1].avg_risk).slice(0, 8);
  const topRoutes = Object.entries(stats?.route_breakdown || {})
    .sort((a, b) => b[1].avg_risk - a[1].avg_risk).slice(0, 8);
  const ageBuckets = Object.entries(stats?.age_distribution || {});

  // ── Processing status phases (data → per-trip AI → portfolio summary) ──────
  const anyProcessing = isRefreshing || aiNarrativeLoading || aiPerTripLoading;
  const perTripSettled = aiPerTripProgress.total > 0 && aiPerTripProgress.done >= aiPerTripProgress.total;
  const statusPhases = [
    {
      key: 'data',
      label: 'Bookings & live Stripe data',
      state: isRefreshing ? 'active' : (assessments.length > 0 ? 'done' : 'pending'),
    },
    {
      key: 'pertrip',
      label: 'Per-trip AI verdicts & live fares',
      state: aiPerTripLoading ? 'active'
        : perTripSettled ? 'done'
        : (isRefreshing ? 'pending' : 'pending'),
      progress: aiPerTripProgress,
    },
    {
      key: 'summary',
      label: 'Portfolio AI summary',
      state: aiNarrativeLoading ? 'active'
        : (aiSummary?.error ? 'failed'
        : (hasUsableAiPortfolioSummary(aiSummary) ? 'done' : 'pending')),
    },
  ];

  return (
    <>
      <Helmet>
        <title>Risk Review — Defera</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      {/* Full-bleed dark shell */}
      <div className="relative min-h-screen bg-[#07070a]">
        {/* Ambient glows */}
        <div
          className="pointer-events-none fixed inset-0 z-0"
          aria-hidden
          style={{
            background: [
              'radial-gradient(ellipse 90% 60% at 50% -30%, rgba(16, 37, 178,0.13) 0%, transparent 55%)',
              'radial-gradient(ellipse 50% 45% at 96% 12%, rgba(16, 37, 178,0.08) 0%, transparent 50%)',
              'radial-gradient(ellipse 45% 38% at 4% 88%, rgba(16,185,129,0.07) 0%, transparent 45%)',
            ].join(','),
          }}
        />

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 md:px-8 pt-6 pb-12">

          {/* ── Hero ─────────────────────────────────────────────────────── */}
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="mb-5 md:mb-6"
          >
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 lg:gap-8">
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-[0.36em] text-brand-200/45 font-medium mb-1.5">
                  Portfolio intelligence
                </p>
                <h1 className="text-2xl sm:text-3xl font-extralight text-white tracking-tight leading-[1.1] flex items-center gap-2 flex-wrap">
                  <ShieldAlert className="w-5 h-5 text-brand-200/65 shrink-0" strokeWidth={1.25} />
                  <span>
                    Risk{' '}
                    <span className="font-normal text-transparent bg-clip-text bg-gradient-to-r from-brand-100 via-stone-200 to-stone-500">
                      Review
                    </span>
                  </span>
                </h1>
                <p className="text-stone-400 text-sm font-normal mt-1.5 leading-relaxed max-w-xl">
                  Unticketed DC &amp; DZ bookings — live Stripe data, default signals, ticket vs. hold.
                </p>
              </div>

              <div className="flex flex-row flex-wrap lg:flex-col gap-1.5 sm:gap-2 shrink-0 lg:items-end">
                <Button
                  onClick={() => load(true, {})}
                  disabled={isRefreshing}
                  className="rounded-full h-8 sm:h-9 px-3 sm:px-5 bg-gradient-to-r from-brand-100 via-brand-200/90 to-brand-300/75 text-zinc-950 hover:opacity-90 border-0 shadow-[0_10px_36px_-12px_rgba(16, 37, 178,0.5)] font-medium tracking-wide shrink-0 text-sm sm:text-base"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                  {isRefreshing ? 'Refreshing…' : 'Refresh + AI'}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => load(true, { dataOnly: true })}
                  disabled={isRefreshing}
                  className="rounded-full h-8 sm:h-9 px-3 sm:px-4 text-stone-400 hover:text-stone-200 border border-white/10 hover:bg-white/[0.04] text-sm sm:text-base"
                >
                  Data only
                </Button>
                <Button
                  variant="ghost"
                  onClick={runCalibrate}
                  disabled={calibrateLoading}
                  className="rounded-full h-8 sm:h-9 px-3 sm:px-4 text-brand-400/80 hover:text-brand-200 border border-brand-400/20 hover:bg-brand-950/30 text-sm sm:text-base"
                >
                  <FlaskConical className={`hidden sm:block w-3.5 h-3.5 mr-1.5 ${calibrateLoading ? 'animate-pulse' : ''}`} />
                  {calibrateLoading ? 'Calibrating…' : 'Calibrate'}
                </Button>
              </div>
            </div>

            {/* Meta pills */}
            {stats?.total_active != null && (
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-stone-500 font-normal">
                <span><span className="text-stone-200 tabular-nums">{stats.total_active}</span> unticketed</span>
                <span className="text-white/10">·</span>
                <span className="text-brand-200/55">Capital <span className="text-stone-300 tabular-nums">{stats.payment_type_breakdown?.defera_capital ?? 0}</span></span>
                <span className="text-emerald-200/55">Zero <span className="text-stone-300 tabular-nums">{stats.payment_type_breakdown?.defera_zero ?? 0}</span></span>
                {stats.stripe_enriched_count != null && (
                  <span className="hidden sm:contents">
                    <span className="text-white/10">·</span>
                    <span>Stripe live <span className="text-stone-300 tabular-nums">{stats.stripe_enriched_count}</span></span>
                  </span>
                )}
                {(stats.ticketed_excluded_count ?? 0) > 0 && (
                  <span className="hidden sm:contents">
                    <span className="text-white/10">·</span>
                    <span><span className="text-stone-400 tabular-nums">{stats.ticketed_excluded_count}</span> ticketed hidden</span>
                  </span>
                )}
                {lastCompletedAt && !anyProcessing && (
                  <>
                    <span className="text-white/10">·</span>
                    <span className="inline-flex items-center gap-1.5">
                      <CheckCircle2 className="w-3 h-3 text-emerald-300/70 shrink-0" strokeWidth={1.5} />
                      {/* Snapshot age is unbounded now that nothing auto-
                          regenerates — a days-old stamp must carry its date
                          (audit F4). */}
                      <span>Updated <span className="text-stone-300">{format(
                        new Date(lastCompletedAt),
                        new Date(lastCompletedAt).toDateString() === new Date().toDateString() ? 'h:mm a' : 'MMM d, h:mm a',
                      )}</span></span>
                    </span>
                  </>
                )}
              </div>
            )}
          </motion.div>

          {/* ── Processing status ───────────────────────────────────────── */}
          {(anyProcessing || statusComplete) && (
            <ProcessingStatus
              phases={statusPhases}
              complete={statusComplete && !anyProcessing}
              completedAt={lastCompletedAt}
            />
          )}

          {/* ── Error ───────────────────────────────────────────────────── */}
          {error && (
            <div className="mb-4 flex items-start gap-3 text-brand-100/90 bg-brand-950/30 border border-brand-500/20 rounded-2xl px-4 py-3 text-base">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-brand-400/80" />
              <span className="font-normal leading-relaxed break-words min-w-0">{error}</span>
            </div>
          )}

          {/* ── Loading spinner ──────────────────────────────────────────── */}
          {isLoading && (
            <div className="py-24 text-center">
              <RefreshCw className="w-7 h-7 text-brand-200/25 animate-spin mx-auto mb-4" />
              <p className="text-stone-500 text-base font-normal tracking-wide">Scoring portfolio…</p>
            </div>
          )}

          {/* ── No stored review yet ─────────────────────────────────────
              The page never self-generates (founder call, 2026-08-04): with
              no server snapshot on file there is nothing to render until
              the admin commands a run. */}
          {!isLoading && !stats && !error && (
            <div className="py-24 text-center">
              <ClipboardList className="w-7 h-7 text-stone-600 mx-auto mb-4" />
              <p className="text-stone-300 text-base font-normal">No stored review yet.</p>
              <p className="text-stone-400 text-sm font-normal mt-1.5 max-w-md mx-auto">
                Hit <span className="text-stone-200">Refresh + AI</span> (or <span className="text-stone-200">Data only</span>)
                to generate one — it stays saved for every device until you run it again.
              </p>
            </div>
          )}

          {/* ── Dashboard ───────────────────────────────────────────────── */}
          {!isLoading && stats && (
            <div className="space-y-3">

              {/* Model Calibration Panel */}
              {showCalibrate && calibrateData && (
                <div className={`${PANEL} overflow-hidden`}>
                  <div className="px-3.5 py-2.5 border-b border-white/[0.06] flex items-center justify-between">
                    <span className="text-white/85 font-normal text-sm flex items-center gap-2">
                      <FlaskConical className="w-3 h-3 text-brand-300/80 shrink-0" strokeWidth={1.25} />
                      Model calibration — defaulted vs completed
                    </span>
                    <button type="button" onClick={() => setShowCalibrate(false)} className="text-stone-600 hover:text-stone-400 text-sm">×</button>
                  </div>
                  <div className="px-3.5 py-3 space-y-3">
                    <p className="text-[13px] text-stone-400 font-normal leading-relaxed">{calibrateData.note}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {[['Defaulted', calibrateData.defaulted, 'text-rose-300/80'], ['Completed', calibrateData.completed, 'text-emerald-300/80']].map(([label, d, color]) => (
                        <div key={label} className={`${INSET} p-2.5`}>
                          <p className={`text-sm font-medium mb-2 ${color}`}>{label} ({d?.count ?? 0} bookings)</p>
                          {d?.count > 0 ? (
                            <div className="space-y-1">
                              {[
                                ['Avg risk score', d.avg_risk_score],
                                ['Avg paid %', d.avg_paid_pct != null ? `${d.avg_paid_pct}%` : '—'],
                                ['Avg deposit %', d.avg_deposit_pct != null ? `${d.avg_deposit_pct}%` : '—'],
                                ['Avg booking age', d.avg_days_since_book != null ? `${d.avg_days_since_book}d` : '—'],
                                ['Avg fare', d.avg_total_amount != null ? `$${d.avg_total_amount.toLocaleString()}` : '—'],
                                ['Avg pax', d.avg_passenger_count],
                                ['Avg youngest age', d.avg_youngest_age],
                                ['% international', d.pct_international != null ? `${d.pct_international}%` : '—'],
                                ['% missing passport', d.pct_missing_passport != null ? `${d.pct_missing_passport}%` : '—'],
                                ['% premium cabin', d.pct_premium_cabin != null ? `${d.pct_premium_cabin}%` : '—'],
                                ['% high fare (>$3k)', d.pct_high_fare != null ? `${d.pct_high_fare}%` : '—'],
                              ].map(([k, v]) => (
                                <div key={k} className="flex items-center justify-between gap-2">
                                  <span className="text-stone-500 text-xs font-normal">{k}</span>
                                  <span className="text-stone-200 text-xs font-mono tabular-nums">{v ?? '—'}</span>
                                </div>
                              ))}
                              {d.risk_score_distribution && (
                                <div className="pt-1 mt-1 border-t border-white/[0.05]">
                                  <p className={`${MICRO} mb-1`}>Score dist.</p>
                                  <div className="flex gap-2 flex-wrap">
                                    {Object.entries(d.risk_score_distribution).map(([tier, pct]) => (
                                      <span key={tier} className="text-xs text-stone-400 font-normal">{tier} {pct}%</span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : <p className="text-stone-600 text-sm font-normal">No {label.toLowerCase()} bookings found.</p>}
                        </div>
                      ))}
                    </div>
                    {/* Top drivers in defaulted bookings */}
                    {calibrateData.defaulted?.top_drivers?.length > 0 && (
                      <div>
                        <p className={`${MICRO} mb-2`}>Most common drivers in defaulted bookings</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                          {calibrateData.defaulted.top_drivers.slice(0, 10).map((d, i) => (
                            <div key={i} className="flex items-center justify-between text-xs bg-zinc-900/50 rounded-lg px-2.5 py-1 border border-white/[0.04] gap-2">
                              <span className="text-stone-400 font-normal min-w-0 truncate">{d.label}</span>
                              <span className="text-stone-500 font-mono shrink-0">{d.pct}%</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Portfolio analytics — the trip table leads the page now (founder
                  ask, 2026-10-02: "a simple chart like this"), so every
                  portfolio panel folds beneath this one bar. Nothing was
                  removed; it opens on click and remembers per viewer. */}
              <div className={`${PANEL} overflow-hidden`}>
                <button
                  type="button"
                  onClick={toggleAnalytics}
                  aria-expanded={showAnalytics}
                  className="w-full px-3.5 py-2.5 flex items-center justify-between gap-3 text-left hover:bg-white/[0.025] transition-colors"
                >
                  <span className="text-white/85 font-normal text-sm flex items-center gap-2 min-w-0">
                    <BarChart2 className="w-3 h-3 text-brand-200/65 shrink-0" strokeWidth={1.25} />
                    Portfolio analytics
                  </span>
                  <span className="flex items-center gap-3 min-w-0">
                    <span className="hidden sm:inline text-xs text-stone-500 font-normal tabular-nums truncate">
                      Exposure <span className="text-stone-300">{fmt$(stats.total_exposure)}</span>
                      <span className="text-stone-700"> · </span>
                      Unpaid <span className="text-stone-300">{fmt$(stats.total_remaining)}</span>
                      <span className="text-stone-700"> · </span>
                      Expected loss <span className="text-rose-300/85">{fmt$(stats.expected_loss)}</span>
                    </span>
                    {showAnalytics
                      ? <ChevronUp className="w-4 h-4 text-stone-500 shrink-0" />
                      : <ChevronDown className="w-4 h-4 text-stone-500 shrink-0" />}
                  </span>
                </button>
              </div>
              {showAnalytics && (
                <div className="space-y-3">
                  {/* KPI row */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                    <KpiCard icon={TrendingUp}   label="Active trips"    value={stats.total_active}                    tone="neutral" />
                    <KpiCard icon={DollarSign}   label="Total exposure"  value={fmt$(stats.total_exposure)}            tone="neutral" />
                    <KpiCard icon={CreditCard}   label="Unpaid"          value={fmt$(stats.total_remaining)}           tone="neutral" />
                    <KpiCard icon={AlertTriangle} label="At risk"        value={fmt$(stats.total_at_risk_value)}       tone="warn"    />
                    <KpiCard icon={XCircle}      label="Expected loss"   value={fmt$(stats.expected_loss)}             tone="danger"  />
                    <KpiCard
                      icon={ShieldAlert}
                      label="Avg score"
                      value={stats.average_risk_score}
                      tone={stats.average_risk_score >= 55 ? 'danger' : stats.average_risk_score >= 35 ? 'warn' : 'good'}
                    />
                  </div>

                  {/* Are we winning the wait? Two square cards: the net position if
                      every unticketed trip were booked today, and which way the
                      curve actually moved. */}
                  {farePosition.pricedCount > 0 && <FarePositionCards fp={farePosition} />}

                  {/* Compact Portfolio Intelligence summary — deep-links to the
                      full Risk Command Center page. */}
                  {portfolioSummary && <PortfolioSummaryCard s={portfolioSummary} />}

                  {/* Empirical Atlas — measured Defera default rates + fare drift.
                      This is what the per-trip AI is now grounded in, so the
                      admin can see the actual priors the model is reasoning from. */}
                  {empiricalAtlas && <AtlasSummaryCard atlas={empiricalAtlas} />}

                  {/* Risk + Timing split */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                    {/* Risk distribution */}
                    <div className={PANEL}>
                      <div className="px-3.5 py-2.5 border-b border-white/[0.06]">
                        <h3 className="text-white/85 font-normal text-sm flex items-center gap-2">
                          <ShieldAlert className="w-3 h-3 text-brand-200/65 shrink-0" strokeWidth={1.25} />
                          Risk distribution
                        </h3>
                      </div>
                      <div className="px-3.5 pb-3.5 pt-2.5 space-y-2">
                        {['critical', 'high', 'medium', 'low'].map((lvl) => {
                          const count = riskDist[lvl] || 0;
                          const pct   = stats.total_active ? Math.round((count / stats.total_active) * 100) : 0;
                          return (
                            <div key={lvl} className="flex items-center gap-3">
                              <span className={`text-xs uppercase tracking-[0.18em] w-16 shrink-0 font-medium ${RISK_TEXT[lvl]}`}>
                                {lvl}
                              </span>
                              <div className="flex-1 h-1.5 bg-zinc-900/70 rounded-full overflow-hidden ring-1 ring-white/[0.05]">
                                <div
                                  className={`h-full rounded-full transition-[width] duration-700 ease-out ${RISK_BAR[lvl]}`}
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                              <span className="text-stone-200 text-sm font-mono tabular-nums w-8 text-right shrink-0">{count}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Booking timing */}
                    <div className={PANEL}>
                      <div className="px-3.5 py-2.5 border-b border-white/[0.06]">
                        <h3 className="text-white/85 font-normal text-sm flex items-center gap-2">
                          <CheckCircle2 className="w-3 h-3 text-emerald-300/75 shrink-0" strokeWidth={1.25} />
                          Booking timing
                        </h3>
                      </div>
                      <div className="px-3.5 pb-2.5 pt-2 space-y-0">
                        {['BOOK_NOW', 'WAIT_FOR_NEXT_PAYMENT', 'HOLD_BOOKING', 'MONITOR'].map((a) => {
                          const Icon = ACTION_ICON[a];
                          return (
                            <div key={a} className="flex items-center justify-between gap-3 py-1 px-2 -mx-2 rounded-lg hover:bg-white/[0.03] transition-colors">
                              <div className="flex items-center gap-2 min-w-0">
                                <Icon className="w-3 h-3 text-stone-500 shrink-0" strokeWidth={1.25} />
                                <span className="text-stone-200 text-sm font-normal">{ACTION_LABEL_FULL[a]}</span>
                              </div>
                              <span className="text-stone-300 text-sm font-mono tabular-nums shrink-0">{recDist[a] || 0}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {aiNarrativeLoading && (
                    <div className={`${PANEL} overflow-hidden`}>
                      <div className="px-3.5 py-3 flex items-center gap-2.5 text-stone-400 text-sm font-normal">
                        <Brain className="w-4 h-4 text-brand-300/60 shrink-0 animate-pulse" strokeWidth={1.25} />
                        <span>
                          Generating AI portfolio assessment…
                          <span className="text-stone-600 text-xs block mt-0.5">
                            Runs as a second server request so the main risk data can finish before GPT-4o (avoids Vercel timeouts).
                          </span>
                        </span>
                      </div>
                    </div>
                  )}

                  {/* AI Portfolio Assessment */}
                  {aiSummary && !aiSummary.error && (
                    <div className={`${PANEL} overflow-hidden`}>
                      <button
                        type="button"
                        onClick={() => setShowAi(!showAi)}
                        className="w-full px-3.5 py-2.5 flex items-center justify-between text-left hover:bg-white/[0.025] transition-colors border-b border-white/[0.06]"
                      >
                        <span className="text-white/85 font-normal text-sm flex items-center gap-2">
                          <Brain className="w-3 h-3 text-brand-300/80 shrink-0" strokeWidth={1.25} />
                          AI portfolio assessment
                        </span>
                        {showAi
                          ? <ChevronUp className="w-4 h-4 text-stone-500 shrink-0" />
                          : <ChevronDown className="w-4 h-4 text-stone-500 shrink-0" />}
                      </button>
                      {showAi && (
                        <div className="px-3.5 py-3 space-y-3">
                          {aiSummary.portfolio_summary && (
                            <p className="text-stone-300 text-sm leading-[1.65] font-normal">{aiSummary.portfolio_summary}</p>
                          )}
                          {aiSummary.booking_timing_advice && (
                            <AiSection label="Timing policy" body={aiSummary.booking_timing_advice} />
                          )}
                          {aiSummary.demographic_signal && (
                            <AiSection label="Demographic signal" body={aiSummary.demographic_signal} />
                          )}
                          {Array.isArray(aiSummary.key_patterns) && aiSummary.key_patterns.length > 0 && (
                            <div>
                              <p className={`${MICRO} mb-2`}>Key patterns</p>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                                {aiSummary.key_patterns.map((p, i) => (
                                  <div key={i} className={`${INSET} p-2.5`}>
                                    <p className="text-white/90 text-sm font-normal mb-0.5">{p.title}</p>
                                    <p className="text-stone-400 text-[13px] font-normal leading-relaxed">{p.description}</p>
                                    {p.impact && <p className="text-brand-200/65 text-xs italic mt-1">{p.impact}</p>}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {Array.isArray(aiSummary.immediate_actions) && aiSummary.immediate_actions.length > 0 && (
                            <div>
                              <p className={`${MICRO} mb-1.5`}>Immediate actions</p>
                              <ul className="space-y-1">
                                {aiSummary.immediate_actions.map((act, i) => (
                                  <li key={i} className="flex gap-2 text-sm font-normal text-stone-300">
                                    <span className="text-brand-200/40 shrink-0 select-none">—</span>
                                    <span>{act}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {aiSummary.projected_loss_reduction_pct != null && (
                            <p className="text-emerald-300/90 text-sm font-normal">
                              Projected loss reduction if policy applied:{' '}
                              <span className="font-medium tabular-nums">{aiSummary.projected_loss_reduction_pct}%</span>
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {aiSummary?.error && (
                    <p className="text-stone-500 text-sm font-normal px-1">AI unavailable: {aiSummary.error}</p>
                  )}

                  {/* Risk Breakdowns */}
                  <div className={`${PANEL} overflow-hidden`}>
                    <button
                      type="button"
                      onClick={() => setShowBreakdowns(!showBreakdowns)}
                      className="w-full px-3.5 py-2.5 flex items-center justify-between text-left hover:bg-white/[0.025] transition-colors"
                    >
                      <span className="text-white/85 font-normal text-sm flex items-center gap-2">
                        <Sparkles className="w-3 h-3 text-brand-200/65 shrink-0" strokeWidth={1.25} />
                        Risk breakdowns
                      </span>
                      {showBreakdowns
                        ? <ChevronUp className="w-4 h-4 text-stone-500 shrink-0" />
                        : <ChevronDown className="w-4 h-4 text-stone-500 shrink-0" />}
                    </button>
                    {showBreakdowns && (
                      <div className="border-t border-white/[0.06] px-3.5 pt-3 pb-3.5 grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <BreakdownCol title="Airlines" rows={topAirlines} />
                        <BreakdownCol title="Routes"   rows={topRoutes}   />
                        <div>
                          <p className={`${MICRO} mb-2`}>Age</p>
                          <div className="space-y-1">
                            {ageBuckets.map(([bucket, count]) => (
                              <div key={bucket} className="flex items-center justify-between gap-2">
                                <span className="text-stone-300 text-sm font-normal">{bucket}</span>
                                <span className="text-stone-300 font-mono text-sm tabular-nums">{count}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Ranking + filters. The two rankings are the founder's
                  priorities (soonest departure / most paid) and sit first.
                  On a phone the four dropdowns fold behind a Filters button,
                  two to a row, so the trips start on the first screen. */}
              <div className={`${PANEL} p-2.5`}>
                <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-1.5">
                  <RankToggle value={sortBy} onChange={setSortBy} />
                  <div className="flex items-center gap-1.5 w-full sm:w-auto sm:flex-1 sm:min-w-[12rem]">
                    <div className="relative flex-1 min-w-0">
                      <SearchIcon className="w-3.5 h-3.5 text-stone-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                      <Input
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search name, ref, route, airline…"
                        className="bg-zinc-900/60 border-white/[0.08] text-stone-100 placeholder:text-stone-600 pl-9 h-8 rounded-xl focus-visible:ring-brand-200/20 text-sm"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowMobileFilters((v) => !v)}
                      aria-expanded={showMobileFilters}
                      className={`sm:hidden h-8 px-3 rounded-xl border text-sm whitespace-nowrap transition-colors ${
                        activeFilterCount > 0
                          ? 'border-brand-400/30 bg-brand-950/40 text-brand-100'
                          : 'border-white/[0.08] bg-zinc-900/60 text-stone-300'
                      }`}
                    >
                      Filters{activeFilterCount > 0 ? ` · ${activeFilterCount}` : ''}
                    </button>
                  </div>
                  <div className={`${showMobileFilters ? 'grid' : 'hidden'} grid-cols-2 gap-1.5 sm:flex sm:flex-wrap sm:items-center`}>
                    <FilterSelect value={riskFilter} onChange={setRiskFilter} width="w-full sm:w-40">
                      <SelectItem value="all">All risk levels</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </FilterSelect>
                    <FilterSelect value={actionFilter} onChange={setActionFilter} width="w-full sm:w-44">
                      <SelectItem value="all">All actions</SelectItem>
                      <SelectItem value="BOOK_NOW">Book Now</SelectItem>
                      <SelectItem value="WAIT_FOR_NEXT_PAYMENT">Wait · Next Pmt</SelectItem>
                      <SelectItem value="HOLD_BOOKING">Hold Booking</SelectItem>
                      <SelectItem value="MONITOR">Monitor</SelectItem>
                    </FilterSelect>
                    <FilterSelect value={paymentTypeFilter} onChange={setPaymentTypeFilter} width="w-full sm:w-36">
                      <SelectItem value="all">All types</SelectItem>
                      <SelectItem value="defera_capital">Capital</SelectItem>
                      <SelectItem value="defera_zero">Zero</SelectItem>
                    </FilterSelect>
                    <FilterSelect value={paidFilter} onChange={setPaidFilter} width="w-full sm:w-36">
                      <SelectItem value="all">Any % paid</SelectItem>
                      <SelectItem value="50">50%+ paid</SelectItem>
                      <SelectItem value="70">70%+ paid</SelectItem>
                      <SelectItem value="90">90%+ paid</SelectItem>
                    </FilterSelect>
                  </div>
                </div>
              </div>

              {/* Count line */}
              <p className="text-stone-500 text-xs font-normal tracking-wide flex items-center gap-2 flex-wrap">
                <span>{filtered.length} of {assessments.length} trips · {SORT_LABELS[sortBy] || SORT_LABELS.departure}</span>
                {/* Freshness of the shared server snapshot — same stamp on
                    every device; only a commanded Refresh moves it. */}
                {snapshotGeneratedAt ? (() => {
                  const mins = Math.round((Date.now() - snapshotGeneratedAt) / 60000);
                  const label = mins < 1 ? 'just now'
                    : mins < 60 ? `${mins}m ago`
                      : mins < 60 * 48 ? `${Math.round(mins / 60)}h ago`
                        : `${Math.round(mins / (60 * 24))}d ago`;
                  return (
                    <span className="inline-flex items-center gap-1 text-xs text-brand-400/50 border border-brand-400/15 rounded-full px-2 py-0.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-brand-400/40 inline-block" />
                      generated · {label}
                    </span>
                  );
                })() : null}
              </p>

              {/* Trip table — Departs · Customer · Ref · Paid · Base · Total ·
                  Base now · Net profit · Last payment, each row one click
                  from its full detail and the per-trip buttons. */}
              {filtered.length === 0 ? (
                <div className={`${PANEL} py-16 text-center`}>
                  <p className="text-stone-400 text-base font-normal">No trips match the current filters.</p>
                </div>
              ) : (
                <RiskTripTable
                  rows={filtered}
                  sortBy={sortBy}
                  onSort={setSortBy}
                  focusedBookingId={focusedBookingId}
                  onDeepDive={openDeepDive}
                  currentUser={currentUser}
                />
              )}
            </div>
          )}
        </div>

        {/* ── Deep dive dialog ──────────────────────────────────────────── */}
        <Dialog open={!!deepDiveTrip} onOpenChange={(open) => !open && setDeepDiveTrip(null)}>
          <DialogContent className="bg-zinc-950/98 backdrop-blur-2xl text-stone-200 border border-white/[0.1] max-w-2xl max-h-[88vh] overflow-y-auto shadow-[0_32px_120px_-40px_rgba(0,0,0,0.95)] rounded-2xl">
            <DialogHeader>
              <DialogTitle className="text-white/95 font-extralight text-2xl tracking-tight flex items-center gap-2.5">
                <Brain className="w-5 h-5 text-brand-300/80 shrink-0" strokeWidth={1.25} />
                Risk deep dive
              </DialogTitle>
              <DialogDescription className="text-stone-400 text-base font-normal pt-1 leading-relaxed">
                {deepDiveTrip?.route} · {deepDiveTrip?.marketing_airline} · {deepDiveTrip?.user_email}
              </DialogDescription>
            </DialogHeader>
            {deepDiveLoading && (
              <div className="py-12 text-center">
                <RefreshCw className="w-6 h-6 animate-spin mx-auto text-brand-200/20 mb-3" />
                <p className="text-stone-500 text-base font-normal tracking-wide">AI is thinking…</p>
              </div>
            )}
            {!deepDiveLoading && deepDiveData?.error && (
              <p className="text-rose-300/90 text-base font-normal">{deepDiveData.error}</p>
            )}
            {!deepDiveLoading && deepDiveData && !deepDiveData.error && (
              <DeepDiveBody data={deepDiveData} />
            )}
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function AiSection({ label, body }) {
  return (
    <div>
      <p className={`${MICRO} mb-1`}>{label}</p>
      <p className="text-stone-300 text-sm font-normal leading-[1.6]">{body}</p>
    </div>
  );
}

// Renders the GPT-4o fare-trajectory assessment: route+airline+cabin+season
// reasoning, structured tier signals, and a "ticket-by" lead time. Lets the
// admin see WHY the AI thinks fare pressure on this specific trip is high or
// low, rather than just a flat calendar threshold.
function FareTrajectorySection({ ft, dtd, route, airline, cabin, history }) {
  if (!ft || typeof ft !== 'object') return null;
  const urgency = (ft.fare_urgency || '').toLowerCase();
  const urgencyPill = FARE_URGENCY_PILL[urgency] || FARE_URGENCY_PILL.medium;
  const urgencyLabel = FARE_URGENCY_LABEL[urgency] || 'Fare urgency';

  const ticketBy = Number.isFinite(ft.recommended_ticket_by_dtd) ? ft.recommended_ticket_by_dtd : null;
  const nextFence = Number.isFinite(ft.next_fare_fence_dtd) ? ft.next_fare_fence_dtd : null;
  const daysLeftToTicket = ticketBy != null && Number.isFinite(dtd) ? dtd - ticketBy : null;

  // Actual recent market price history for this route/dates (the same series
  // the AI studied). Render a sparkline so the admin can eyeball the trend.
  const histVals = (Array.isArray(history) ? history : [])
    .map((p) => Number(p?.c))
    .filter((c) => Number.isFinite(c) && c > 0);
  const hasHistory = histVals.length >= 2;
  const histMin = hasHistory ? Math.min(...histVals) : null;
  const histMax = hasHistory ? Math.max(...histVals) : null;
  const histNow = hasHistory ? histVals[histVals.length - 1] : null;
  const fmtCents0 = (c) => fmt$(Math.round((c ?? 0) / 100));

  return (
    <div className={`${INSET} p-3 space-y-3`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <TrendingUp className="w-3.5 h-3.5 text-brand-200/60 shrink-0" strokeWidth={1.5} />
          <p className={`${MICRO}`}>Fare trajectory · AI</p>
        </div>
        <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium border ${urgencyPill}`}>
          {urgencyLabel}
        </span>
      </div>

      {(route || airline || cabin) && (
        <p className="text-xs text-stone-500 font-normal leading-snug">
          {[airline, route, cabin].filter(Boolean).join(' · ')}
        </p>
      )}

      {hasHistory && (
        <div className="space-y-1">
          <div className="flex items-center justify-between gap-2">
            <p className={MICRO}>Market history · this route</p>
            <span className="text-[11px] text-stone-500 font-normal tabular-nums">{histVals.length} pts</span>
          </div>
          <Sparkline points={history} className="w-full h-10" />
          <div className="flex items-center justify-between gap-2 text-[10.5px] text-stone-500 font-normal tabular-nums">
            <span>Low {fmtCents0(histMin)}</span>
            <span className="text-stone-300">Now {fmtCents0(histNow)}</span>
            <span>High {fmtCents0(histMax)}</span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-2">
        <FareSignal label="Route volatility" value={fmtTier(ft.route_volatility_tier)} tone={VOLATILITY_TONE[ft.route_volatility_tier]} />
        <FareSignal label="Seasonal demand" value={fmtTier(ft.seasonal_demand)} tone={SEASONAL_TONE[ft.seasonal_demand]} />
        <FareSignal label="Competition"     value={fmtTier(ft.competition_density)} tone={COMPETITION_TONE[ft.competition_density]} />
        <FareSignal label="Cabin scarcity"  value={fmtTier(ft.cabin_scarcity)} tone={SCARCITY_TONE[ft.cabin_scarcity]} />
      </div>

      {(ticketBy != null || nextFence != null) && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-stone-400 font-normal pt-1">
          {ticketBy != null && (
            <span>
              Ticket by{' '}
              <span className="font-mono tabular-nums text-stone-200">{ticketBy}d</span> before departure
              {daysLeftToTicket != null && (
                <span className={`ml-1 ${daysLeftToTicket <= 0 ? 'text-rose-300/90' : daysLeftToTicket <= 5 ? 'text-brand-200/85' : 'text-stone-500'}`}>
                  ({daysLeftToTicket <= 0 ? 'overdue' : `${daysLeftToTicket}d runway`})
                </span>
              )}
            </span>
          )}
          {nextFence != null && (
            <span>
              Next fare fence{' '}
              <span className="font-mono tabular-nums text-stone-200">{nextFence}d</span> out
            </span>
          )}
        </div>
      )}

      {ft.reasoning && (
        <p className="text-stone-300 text-sm font-normal leading-[1.6] border-t border-white/[0.06] pt-2">
          {ft.reasoning}
        </p>
      )}
    </div>
  );
}

function FareSignal({ label, value, tone }) {
  return (
    <div className="min-w-0">
      <p className={MICRO}>{label}</p>
      <p className={`text-sm font-normal mt-0.5 truncate ${tone || 'text-stone-300'}`}>{value}</p>
    </div>
  );
}

/** Tiny inline price-history sparkline. `points` is an array of {t,c} (cents)
 *  or plain numbers — the actual recent market fares for this route/dates that
 *  the AI also reasons over. Stroke colour reflects net direction (orange =
 *  ended higher, emerald = ended lower). `preserveAspectRatio="none"` plus a
 *  Tailwind width class (e.g. w-full) lets it stretch responsively on mobile
 *  while the viewBox keeps the curve crisp. Returns null with < 2 usable points. */
function Sparkline({ points, width = 96, height = 24, className = '', strokeWidth = 1.25 }) {
  const vals = (Array.isArray(points) ? points : [])
    .map((p) => (typeof p === 'number' ? p : Number(p?.c)))
    .filter((n) => Number.isFinite(n) && n > 0);
  if (vals.length < 2) return null;

  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const span = max - min || 1;
  const n = vals.length;
  const px = (i) => (i / (n - 1)) * width;
  const py = (v) => height - ((v - min) / span) * (height - 2) - 1;

  const line = vals.map((v, i) => `${i === 0 ? 'M' : 'L'}${px(i).toFixed(1)},${py(v).toFixed(1)}`).join(' ');
  const area = `${line} L${width.toFixed(1)},${height} L0,${height} Z`;
  const rising = vals[n - 1] >= vals[0];
  const stroke = rising ? '#fb923c' : '#34d399';
  const fill   = rising ? 'rgba(251,146,60,0.12)' : 'rgba(52,211,153,0.12)';

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width={width}
      height={height}
      preserveAspectRatio="none"
      className={className}
      aria-hidden="true"
    >
      <path d={area} fill={fill} stroke="none" />
      <path
        d={line}
        fill="none"
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
      <circle cx={px(n - 1)} cy={py(vals[n - 1])} r="1.6" fill={stroke} />
    </svg>
  );
}

function BreakdownCol({ title, rows }) {
  return (
    <div>
      <p className={`${MICRO} mb-2`}>{title}</p>
      <div className="space-y-1">
        {rows.length === 0 ? (
          <p className="text-stone-600 text-sm font-normal">—</p>
        ) : (
          rows.map(([name, d]) => (
            <div key={name} className="flex items-center justify-between gap-2">
              <span className="text-stone-300 text-sm font-normal truncate min-w-0">{name}</span>
              <span className="text-stone-300 font-mono text-sm tabular-nums shrink-0">{d.avg_risk} · {d.count}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function FilterSelect({ value, onChange, width, children }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={`bg-zinc-900/60 border-white/[0.08] text-stone-200 h-8 rounded-xl text-sm ${width} shrink-0`}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="bg-zinc-950 border-white/10 text-stone-200">
        {children}
      </SelectContent>
    </Select>
  );
}

const RANK_OPTIONS = [
  { value: 'departure', label: 'Soonest departure' },
  { value: 'pct_paid', label: 'Most paid' },
];

/** The two rankings, one tap apart (see RANKINGS in RiskReview.tripTable). */
function RankToggle({ value, onChange }) {
  const current = value === 'pct_paid' ? 'pct_paid' : 'departure';
  return (
    <div
      role="radiogroup"
      aria-label="Rank trips by"
      className="flex w-full sm:w-auto h-8 shrink-0 rounded-xl border border-white/[0.08] bg-zinc-900/60 p-0.5"
    >
      {RANK_OPTIONS.map((o) => {
        const active = current === o.value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(o.value)}
            className={`flex-1 sm:flex-none px-3 rounded-[10px] text-sm whitespace-nowrap transition-colors ${
              active ? 'bg-white/[0.09] text-stone-100 font-medium' : 'text-stone-400 hover:text-stone-200'
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// ── Processing status panel ────────────────────────────────────────────────
// Surfaces each pipeline phase (data fetch → per-trip AI + live fares →
// portfolio summary) so the admin knows exactly what's still working vs. done,
// instead of waiting blindly. Stacks cleanly on mobile.
function PhaseRow({ phase }) {
  const { label, state, progress } = phase;
  const Icon = state === 'active' ? RefreshCw
    : state === 'done' ? CheckCircle2
    : state === 'failed' ? AlertTriangle
    : Clock;
  const iconCls = state === 'active' ? 'text-brand-200/80 animate-spin'
    : state === 'done' ? 'text-emerald-300/90'
    : state === 'failed' ? 'text-rose-300/90'
    : 'text-stone-600';
  const labelCls = state === 'pending' ? 'text-stone-500' : 'text-stone-200';
  const hasProg = progress && progress.total > 0;
  const pct = hasProg ? Math.min(100, Math.round((progress.done / progress.total) * 100)) : null;
  const rightText = hasProg ? `${progress.done}/${progress.total}`
    : state === 'active' ? 'Working…'
    : state === 'done' ? 'Done'
    : state === 'failed' ? 'Failed'
    : 'Queued';
  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Icon className={`w-3.5 h-3.5 shrink-0 ${iconCls}`} strokeWidth={1.5} />
          <span className={`text-xs font-normal truncate ${labelCls}`}>{label}</span>
        </div>
        <span className="text-[11px] tabular-nums text-stone-400 shrink-0 inline-flex items-center gap-1">
          {rightText}
          {hasProg && progress.errors > 0 && (
            <span className="text-rose-300/80" title={`${progress.errors} failed`}>· {progress.errors} err</span>
          )}
        </span>
      </div>
      {pct != null && state !== 'pending' && (
        <div className="mt-1.5 h-[3px] w-full rounded-full bg-white/[0.06] overflow-hidden">
          <div
            className={`h-full rounded-full transition-[width] duration-500 ease-out ${
              state === 'failed' ? 'bg-rose-500/70'
              : state === 'done' ? 'bg-emerald-500/80'
              : 'bg-gradient-to-r from-brand-500 to-brand-300'
            }`}
            style={{ width: `${state === 'done' ? 100 : pct}%` }}
          />
        </div>
      )}
    </div>
  );
}

function ProcessingStatus({ phases, complete, completedAt }) {
  const total = phases.length;
  const done = phases.filter((p) => p.state === 'done').length;
  return (
    <div className={`${PANEL} p-3 mb-3`}>
      <div className="flex items-center justify-between gap-2 mb-2.5">
        <div className="flex items-center gap-2 min-w-0">
          {complete
            ? <CheckCircle2 className="w-4 h-4 text-emerald-300/90 shrink-0" strokeWidth={1.5} />
            : <RefreshCw className="w-4 h-4 text-brand-200/80 animate-spin shrink-0" strokeWidth={1.5} />}
          <span className="text-sm font-normal text-white/90 truncate">
            {complete ? 'All processing complete' : 'Processing Risk Review…'}
          </span>
        </div>
        <span className="text-[11px] tabular-nums text-stone-500 shrink-0">
          {complete && completedAt
            ? `Updated ${format(new Date(completedAt), 'h:mm a')}`
            : `${done}/${total} steps`}
        </span>
      </div>
      <div className="space-y-2">
        {phases.map((p) => <PhaseRow key={p.key} phase={p} />)}
      </div>
    </div>
  );
}

// ─── Fare position ──────────────────────────────────────────────────────────
// The scoreboard for this page's core bet: every trip here is one we have NOT
// booked yet, so holding is a position on the fare. Two square cards —
// left says whether the position is up or down, right says how it got there.
//
// Sign convention throughout: POSITIVE = good for Defera (the trip costs less
// to book than the customer's plan assumed).
function FarePositionCards({ fp }) {
  const surplus = fp.netCents >= 0;
  const money = (c) => `${c < 0 ? '−' : ''}${fmt$(Math.abs(c) / 100)}`;
  // Split bar: how much of the moved money is working for vs against us.
  const movedCents = fp.gainCents + Math.abs(fp.lossCents);
  const gainShare = movedCents > 0 ? (fp.gainCents / movedCents) * 100 : 0;
  // Share of the fee buffer a deficit consumes. A surplus uses none of it.
  const cushionUsedPct = fp.cushionCents > 0 && fp.netCents < 0
    ? (Math.abs(fp.netCents) / fp.cushionCents) * 100
    : 0;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
      {/* ── Card 1 — net position ─────────────────────────────────────── */}
      <div className={`${PANEL} md:aspect-square flex flex-col`}>
        <div className="px-4 py-3 border-b border-white/[0.06] flex items-center justify-between gap-2">
          <h3 className="text-white/85 font-normal text-sm flex items-center gap-2">
            <Scale className="w-3 h-3 text-brand-200/65 shrink-0" strokeWidth={1.25} />
            If we booked everything today
          </h3>
          <span className="text-xs text-stone-500 tabular-nums shrink-0">
            {fp.pricedCount}/{fp.totalCount} priced
          </span>
        </div>

        <div className="flex-1 flex flex-col justify-center px-4 py-4 min-h-0">
          <p className={`text-xs uppercase tracking-[0.18em] font-medium ${surplus ? 'text-emerald-300/75' : 'text-rose-300/70'}`}>
            {surplus ? 'Surplus' : 'Deficit'}
          </p>
          <p className={`mt-1 text-[2.75rem] leading-none font-extralight tabular-nums ${surplus ? 'text-emerald-200/95' : 'text-rose-200/95'}`}>
            {money(fp.netCents)}
          </p>
          <p className="mt-1.5 text-sm text-stone-400 font-normal">
            {surplus ? 'less' : 'more'} than the base fares these plans were built on
            <span className="text-stone-500"> · {Math.abs(fp.pctOfCommitted).toFixed(1)}%</span>
          </p>

          <div className="mt-5 space-y-1.5 text-sm">
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-stone-500 font-normal">Customers committed</span>
              <span className="text-stone-300 tabular-nums">{fmt$(fp.committedCents / 100)}</span>
            </div>
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-stone-500 font-normal">Cost to book today</span>
              <span className="text-stone-300 tabular-nums">{fmt$(fp.todayCents / 100)}</span>
            </div>
          </div>

          {/* How much of the buffer this position eats. The question behind the
              headline number is never "are we down" — it is "do we have room". */}
          {fp.cushionCents > 0 && (
            <div className="mt-5">
              <div className="flex items-baseline justify-between gap-3 mb-1.5">
                <span className="text-xs uppercase tracking-[0.16em] font-medium text-stone-500">
                  Cushion used
                </span>
                <span className="text-sm text-stone-300 tabular-nums">
                  {cushionUsedPct.toFixed(0)}%
                </span>
              </div>
              <div className="h-1.5 rounded-full overflow-hidden ring-1 ring-white/[0.05] bg-zinc-900/70">
                <div
                  className={`h-full rounded-full transition-[width] duration-700 ease-out ${
                    cushionUsedPct >= 100 ? 'bg-rose-400/80'
                      : cushionUsedPct >= 60 ? 'bg-brand-300/70'
                        : 'bg-emerald-400/70'
                  }`}
                  style={{ width: `${Math.min(100, cushionUsedPct)}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* The hold fee is the buffer a fare rise eats first — a deficit is only
            a real loss once it chews past it. Spelled out as a verdict rather
            than left as arithmetic: the number that matters is not the deficit,
            it is whether the fees still cover it. */}
        <div className="px-4 py-2.5 border-t border-white/[0.06] space-y-1">
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-xs text-stone-500 font-normal">
              After {fmt$(fp.cushionCents / 100)} of service + hold fees
            </span>
            <span className={`text-sm tabular-nums ${fp.afterCushionCents >= 0 ? 'text-emerald-300/85' : 'text-rose-300/85'}`}>
              {money(fp.afterCushionCents)} {fp.afterCushionCents >= 0 ? 'clear' : 'short'}
            </span>
          </div>
          <p className="text-xs text-stone-500 font-normal leading-relaxed">
            {fp.afterCushionCents >= 0 ? (
              <>
                <span className="text-emerald-300/80">Still profitable.</span>{' '}
                {surplus
                  ? `Fares are running ${Math.abs(fp.pctOfCommitted).toFixed(1)}% below the booked base, so the fees are margin on top.`
                  : `Fares are running ${Math.abs(fp.pctOfCommitted).toFixed(1)}% above the booked base, but service + hold fees cover it ${(fp.cushionCents / Math.abs(fp.netCents)).toFixed(1)}× over — not underwater.`}
              </>
            ) : (
              <>
                <span className="text-rose-300/80">Underwater.</span>{' '}
                Fares are {Math.abs(fp.pctOfCommitted).toFixed(1)}% above the booked base — past what
                service + hold fees can absorb. Booking the queue today realizes the shortfall.
              </>
            )}
          </p>
        </div>
      </div>

      {/* ── Card 2 — which way the curve moved ────────────────────────── */}
      <div className={`${PANEL} md:aspect-square flex flex-col`}>
        <div className="px-4 py-3 border-b border-white/[0.06] flex items-center justify-between gap-2">
          <h3 className="text-white/85 font-normal text-sm flex items-center gap-2">
            <TrendingUp className="w-3 h-3 text-brand-200/65 shrink-0" strokeWidth={1.25} />
            Which way the curve moved
          </h3>
          {fp.exactMatches < fp.pricedCount && (
            <span
              className="text-xs text-stone-500 tabular-nums shrink-0"
              title="Trips where the live quote matched the exact booked flight. The rest are the closest comparable itinerary."
            >
              {fp.exactMatches}/{fp.pricedCount} exact
            </span>
          )}
        </div>

        <div className="flex-1 flex flex-col justify-center px-4 py-4 min-h-0">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs uppercase tracking-[0.18em] font-medium text-emerald-300/75">Cheaper now</p>
              <p className="mt-1 text-3xl leading-none font-extralight text-emerald-200/95 tabular-nums">
                {fp.cheaperCount}
              </p>
              <p className="mt-1 text-sm text-stone-400 tabular-nums">{fmt$(fp.gainCents / 100)} saved</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.18em] font-medium text-rose-300/70">Pricier now</p>
              <p className="mt-1 text-3xl leading-none font-extralight text-rose-200/95 tabular-nums">
                {fp.pricierCount}
              </p>
              <p className="mt-1 text-sm text-stone-400 tabular-nums">{money(fp.lossCents)} added</p>
            </div>
          </div>

          {movedCents > 0 && (
            <div className="mt-4 flex h-1.5 rounded-full overflow-hidden ring-1 ring-white/[0.05] bg-zinc-900/70">
              <div className="h-full bg-emerald-400/70" style={{ width: `${gainShare}%` }} />
              <div className="h-full bg-rose-400/70" style={{ width: `${100 - gainShare}%` }} />
            </div>
          )}

          {/* Names, not just totals — a $900 swing on one route is a decision,
              an aggregate is only a mood. */}
          <div className="mt-5 grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
            <p className="text-xs uppercase tracking-[0.16em] font-medium text-rose-300/70">Biggest rises</p>
            <p className="text-xs uppercase tracking-[0.16em] font-medium text-emerald-300/75">Biggest drops</p>
            <div className="space-y-1 min-w-0">
              {fp.topBleeders.map((t) => (
                <div key={t.booking_id} className="flex items-baseline justify-between gap-2">
                  <span className="text-stone-400 font-normal truncate">{t.route}</span>
                  <span className="text-rose-300/85 tabular-nums shrink-0">{money(t.delta)}</span>
                </div>
              ))}
              {fp.topBleeders.length === 0 && <span className="text-stone-600">none</span>}
            </div>
            <div className="space-y-1 min-w-0">
              {fp.topSavers.map((t) => (
                <div key={t.booking_id} className="flex items-baseline justify-between gap-2">
                  <span className="text-stone-400 font-normal truncate">{t.route}</span>
                  <span className="text-emerald-300/85 tabular-nums shrink-0">{money(t.delta)}</span>
                </div>
              ))}
              {fp.topSavers.length === 0 && <span className="text-stone-600">none</span>}
            </div>
          </div>
        </div>

        <div className="px-4 py-2.5 border-t border-white/[0.06] text-xs text-stone-500 font-normal">
          {fp.flatCount > 0 && <>{fp.flatCount} unchanged · </>}
          {fp.totalCount - fp.pricedCount > 0
            ? `${fp.totalCount - fp.pricedCount} trips have no live fare and are excluded`
            : 'every unticketed trip priced'}
        </div>
      </div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, tone }) {
  const grad = {
    danger:  'from-rose-500/25 via-rose-500/8 to-transparent',
    warn:    'from-brand-400/22 via-brand-400/6 to-transparent',
    good:    'from-emerald-400/22 via-emerald-400/6 to-transparent',
    neutral: 'from-white/10 via-white/[0.03] to-transparent',
  }[tone] || 'from-white/10 via-white/[0.03] to-transparent';

  const labelColor = {
    danger:  'text-rose-300/70',
    warn:    'text-brand-200/70',
    good:    'text-emerald-300/75',
    neutral: 'text-stone-500',
  }[tone] || 'text-stone-500';

  return (
    <div className={`rounded-2xl p-px bg-gradient-to-br ${grad}`}>
      <div className="rounded-2xl bg-zinc-950/85 border border-white/[0.055] px-2.5 py-2.5 h-full flex flex-col gap-1">
        <div className={`flex items-center gap-1.5 text-xs uppercase tracking-[0.14em] font-medium ${labelColor}`}>
          <Icon className="w-2.5 h-2.5 shrink-0" strokeWidth={1.25} />
          <span>{label}</span>
        </div>
        <p className="text-lg font-extralight text-white/90 leading-none tabular-nums">
          {value}
        </p>
      </div>
    </div>
  );
}

// ─── Empirical Atlas summary card ───────────────────────────────────────────
// Shows the measured Defera priors that anchor every per-trip AI verdict:
// historical default rates by paid-% reached and measured fare drift by
// route+cabin+dtd. This is the data that lets the AI beat generic credit
// heuristics — admins should be able to see what the model is seeing.
function AtlasSummaryCard({ atlas }) {
  if (!atlas || atlas.error) {
    return (
      <div className={`${PANEL} px-3.5 py-2.5 text-xs text-stone-500 font-normal`}>
        <span className="text-brand-200/70">Measured priors</span>
        <span className="text-stone-700 mx-2">·</span>
        Atlas unavailable{atlas?.error ? ` — ${atlas.error}` : ''}
      </div>
    );
  }
  const d = atlas.defaults || {};
  const fd = atlas.fare_drift || {};
  const totalTerminal  = d.sample_sizes?.total_terminal ?? 0;
  const totalActive    = d.sample_sizes?.total_active ?? 0;
  const totalBookings  = d.sample_sizes?.total_bookings_in_window ?? (totalTerminal + totalActive);
  const defaultedN     = d.sample_sizes?.defaulted ?? 0;
  const fareCheckN     = fd.sample_sizes?.total_checks ?? 0;
  const baselineRate   = d.baseline_default_rate_pct;
  const medianAtDef    = d.median_paid_pct_at_default;
  const paidReached    = Array.isArray(d.default_rate_by_paid_reached) ? d.default_rate_by_paid_reached : [];
  const statusBreakdown = d.status_breakdown || {};
  const activeSignals  = d.active_signals || null;

  // Pick the 3 most useful drift slices: highest sample size that show
  // material drift. Caps at 4 for the strip.
  const topDriftSlices = (fd.drift_by_region_cabin_dtd || [])
    .slice()
    .sort((a, b) => (b.median_drift_pct || 0) - (a.median_drift_pct || 0))
    .filter((s) => s.n >= (fd.min_slice_size || 5))
    .slice(0, 4);

  // Order status entries for display: known active first, then known terminal,
  // then anything unexpected. Skips zero counts.
  const STATUS_ORDER = ['confirmed', 'pending', 'in_progress', 'completed', 'delinquent', 'defaulted', 'cancelled', 'refunded'];
  const STATUS_TONE = {
    confirmed:   'text-emerald-300/90',
    pending:     'text-brand-200/85',
    in_progress: 'text-brand-300/85',
    completed:   'text-emerald-300/90',
    delinquent:  'text-amber-300/90',
    defaulted:   'text-rose-300/95',
    cancelled:   'text-stone-400',
    refunded:    'text-stone-400',
  };
  const knownStatuses = STATUS_ORDER.filter((s) => statusBreakdown[s]);
  const otherStatuses = Object.keys(statusBreakdown).filter((s) => !STATUS_ORDER.includes(s));
  const orderedStatuses = [...knownStatuses, ...otherStatuses];

  const paidVelocity = Array.isArray(activeSignals?.paid_velocity_by_age) ? activeSignals.paid_velocity_by_age : [];
  const stalled = activeSignals?.stalled_leading_indicator || null;

  return (
    <div className={`${PANEL} overflow-hidden`}>
      <div className="px-3.5 py-2.5 border-b border-white/[0.06] flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <Brain className="w-3.5 h-3.5 text-emerald-300/80 shrink-0" strokeWidth={1.25} />
          <span className="text-white/85 font-normal text-sm">Measured Defera priors</span>
          <span className="text-xs text-stone-500 font-normal">last {d.data_window_months || 24} months</span>
          {totalTerminal === 0 && totalActive > 0 && (
            <span className="text-xs font-medium text-brand-200/80 bg-brand-950/45 border border-brand-400/25 px-2 py-0.5 rounded-md">
              No terminal outcomes yet · using active-portfolio signals
            </span>
          )}
          {totalTerminal === 0 && totalActive === 0 && totalBookings === 0 && (
            <span className="text-xs font-normal text-stone-400 bg-zinc-900/60 border border-white/[0.08] px-2 py-0.5 rounded-md">
              No bookings in window
            </span>
          )}
        </div>
        <div className="text-xs text-stone-500 font-normal flex items-center gap-3 flex-wrap">
          <span><span className="text-stone-300 tabular-nums">{totalBookings}</span> total</span>
          <span><span className="text-stone-300 tabular-nums">{totalActive}</span> active · <span className="text-stone-300 tabular-nums">{totalTerminal}</span> terminal</span>
          {defaultedN > 0 && <span><span className="text-rose-300/85 tabular-nums">{defaultedN}</span> defaulted</span>}
          {fareCheckN > 0 && <span><span className="text-stone-300 tabular-nums">{fareCheckN}</span> price checks</span>}
          {baselineRate != null && <span>baseline <span className="text-brand-200/85 tabular-nums">{baselineRate}%</span></span>}
        </div>
      </div>

      {/* Status breakdown — always shown when we have any bookings. Makes the
          empty-terminal case understandable instead of mysterious. */}
      {orderedStatuses.length > 0 && (
        <div className="px-3.5 py-2 border-b border-white/[0.04] flex items-center gap-3 flex-wrap text-xs">
          <span className={MICRO}>Status</span>
          <div className="flex items-center gap-2.5 flex-wrap">
            {orderedStatuses.map((s) => (
              <span key={s} className="flex items-center gap-1 font-normal">
                <span className={`tabular-nums ${STATUS_TONE[s] || 'text-stone-300'}`}>{statusBreakdown[s]}</span>
                <span className="text-stone-500">{s}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {totalTerminal > 0 && (
        <div className="px-3.5 py-2.5 grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* Default rate by paid % reached */}
          <div>
            <p className={`${MICRO} mb-1.5`}>Forward default rate · by paid % reached</p>
            <div className="flex items-end gap-2 flex-wrap">
              {paidReached.map((b) => {
                const rate = b.default_rate_pct;
                const tone = rate == null ? 'text-stone-600'
                  : rate >= 15 ? 'text-rose-300/95'
                  : rate >= 8  ? 'text-orange-300/90'
                  : rate >= 3  ? 'text-brand-200/90'
                  : 'text-emerald-300/90';
                const insufficient = b.n < 20;
                return (
                  <div key={b.paid_pct_at_least} className="text-center min-w-[44px]" title={`n=${b.n} (${b.defaulted} defaulted)`}>
                    <p className="text-[10px] uppercase tracking-[0.12em] text-stone-600">≥{b.paid_pct_at_least}%</p>
                    <p className={`font-mono tabular-nums text-sm font-medium mt-0.5 ${tone}`}>
                      {rate == null ? '—' : `${rate}%`}
                    </p>
                    <p className={`text-[10px] font-normal mt-0.5 ${insufficient ? 'text-brand-200/50' : 'text-stone-600'}`}>n={b.n}</p>
                  </div>
                );
              })}
            </div>
            {medianAtDef != null && (
              <p className="text-[12.5px] text-stone-400 font-normal mt-1.5">
                Median paid % when default actually happens: <span className="text-stone-300 tabular-nums">{medianAtDef}%</span>
              </p>
            )}
          </div>

          {/* Fare drift highlights */}
          <div>
            <p className={`${MICRO} mb-1.5`}>Measured fare drift · top slices</p>
            {topDriftSlices.length === 0 ? (
              <p className="text-stone-600 text-xs font-normal">
                {fareCheckN === 0
                  ? 'No price-check history yet. Atlas will populate as admins run live price checks.'
                  : 'No slices with sufficient sample size yet.'}
              </p>
            ) : (
              <div className="space-y-1">
                {topDriftSlices.map((s, i) => {
                  const m = s.median_drift_pct;
                  const tone = m >= 15 ? 'text-rose-300/95'
                    : m >= 8 ? 'text-orange-300/90'
                    : m >= 3 ? 'text-brand-200/90'
                    : m <= -3 ? 'text-emerald-300/90'
                    : 'text-stone-400';
                  return (
                    <div key={i} className="flex items-center justify-between gap-2 text-xs bg-zinc-900/45 rounded-lg px-2.5 py-1 border border-white/[0.04]">
                      <span className="text-stone-300 font-normal min-w-0 truncate">
                        {fmtTier(s.region)} · {fmtTier(s.cabin_tier)} · {s.dtd_band}
                      </span>
                      <span className="flex items-center gap-2 shrink-0">
                        <span className={`font-mono tabular-nums ${tone}`}>{m >= 0 ? '+' : ''}{m}%</span>
                        <span className="text-stone-600 tabular-nums text-[10px]">n={s.n}</span>
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Active-portfolio signals — only shown when we have an active book.
          These are real Defera-measured proxies the AI uses for default-risk
          reasoning before any booking reaches a terminal state. Velocity and
          stalled-rate require a refreshed `_paid_cents_cache`; bookings
          without one are excluded so the sample is honest (not "deposit-only"
          phantoms). When coverage is low, we surface that so admins know
          why n is small. */}
      {totalActive > 0 && activeSignals && (
        <div className="px-3.5 py-2.5 border-t border-white/[0.04] grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <div className="flex items-baseline justify-between gap-2 mb-1.5">
              <p className={MICRO}>Paid-% velocity · by booking age</p>
              {activeSignals.n_active_with_paid_data != null && activeSignals.n_active_with_paid_data < totalActive && (
                <span className="text-[10px] text-brand-200/70 font-normal">
                  {activeSignals.n_active_with_paid_data}/{totalActive} have refreshed paid data
                  {activeSignals.paid_data_coverage_pct != null && (
                    <> ({activeSignals.paid_data_coverage_pct}%)</>
                  )}
                </span>
              )}
            </div>
            {activeSignals.n_active_with_paid_data === 0 ? (
              <p className="text-stone-600 text-xs font-normal">
                No active bookings have a refreshed paid-cents cache yet. Visit the admin Bookings page to populate the cache, then refresh.
              </p>
            ) : (
              <>
                <div className="flex items-end gap-2 flex-wrap">
                  {paidVelocity.map((b) => {
                    const med = b.median_paid_pct;
                    const insufficient = (b.n || 0) < (d.min_slice_size || 5);
                    return (
                      <div key={b.age_band} className="text-center min-w-[52px]" title={b.n ? `n=${b.n} · p25 ${b.p25_paid_pct ?? '—'}% · p75 ${b.p75_paid_pct ?? '—'}%` : 'no data in this band'}>
                        <p className="text-[10px] uppercase tracking-[0.12em] text-stone-600">{b.age_band}</p>
                        <p className={`font-mono tabular-nums text-sm font-medium mt-0.5 ${insufficient ? 'text-stone-600' : 'text-stone-200'}`}>
                          {med == null ? '—' : `${med}%`}
                        </p>
                        <p className={`text-[10px] font-normal mt-0.5 ${insufficient ? 'text-brand-200/50' : 'text-stone-600'}`}>n={b.n || 0}</p>
                      </div>
                    );
                  })}
                </div>
                <p className="text-[12.5px] text-stone-400 font-normal mt-1.5">
                  Median paid % at each booking-age band, computed from {activeSignals.n_active_with_paid_data} active bookings with refreshed paid data.
                </p>
              </>
            )}
          </div>

          {stalled && (
            <div>
              <p className={`${MICRO} mb-1.5`}>Stalled-at-deposit · leading indicator</p>
              {stalled.n_eligible === 0 ? (
                <p className="text-stone-600 text-xs font-normal">
                  {activeSignals.n_active_with_paid_data === 0
                    ? `No refreshed paid data yet — visit /admin/bookings to populate the cache.`
                    : `No bookings older than ${stalled.min_age_days}d with refreshed paid data yet.`}
                </p>
              ) : (
                <div className="space-y-1.5">
                  <div className="flex items-baseline gap-2">
                    <span className={`font-mono tabular-nums text-2xl font-light ${
                      stalled.stalled_rate_pct == null ? 'text-stone-600'
                      : stalled.stalled_rate_pct >= 25 ? 'text-rose-300/95'
                      : stalled.stalled_rate_pct >= 12 ? 'text-orange-300/90'
                      : stalled.stalled_rate_pct >= 5  ? 'text-brand-200/90'
                      : 'text-emerald-300/90'
                    }`}>
                      {stalled.stalled_rate_pct == null ? '—' : `${stalled.stalled_rate_pct}%`}
                    </span>
                    <span className="text-xs text-stone-500 font-normal">
                      stalled at ≤{stalled.threshold_paid_pct}% paid after {stalled.min_age_days}d
                    </span>
                  </div>
                  <p className="text-[12.5px] text-stone-400 font-normal">
                    <span className="text-stone-300 tabular-nums">{stalled.n_stalled}</span>
                    {' of '}
                    <span className="text-stone-300 tabular-nums">{stalled.n_eligible}</span>
                    {' mature bookings have not progressed past the deposit. Strongest pre-terminal default proxy in your active book.'}
                  </p>
                  {stalled.coverage_note && (
                    <p className="text-[10px] text-brand-200/60 font-normal italic">
                      Coverage: {stalled.coverage_note}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Compact Portfolio Intelligence summary card ────────────────────────────
// Renders a single-row card with Health / LCR / Compression / Critical alerts.
// Always deep-links to the full Risk Command Center page.
function PortfolioSummaryCard({ s }) {
  const lcrLabel = (s.liquidity_coverage_ratio_14d == null || !Number.isFinite(s.liquidity_coverage_ratio_14d))
    ? '—'
    : s.liquidity_coverage_ratio_14d.toFixed(2);
  const compressionLabel = s.days_until_liquidity_compression == null
    ? '— '
    : `${s.days_until_liquidity_compression}d`;
  const reserveGap = Number(s.reserve_gap || 0);

  return (
    <div className={`${PANEL} overflow-hidden`}>
      <div className="px-3.5 py-2.5 border-b border-white/[0.06] flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <Gauge className="w-3.5 h-3.5 text-brand-300/80 shrink-0" strokeWidth={1.25} />
          <span className="text-white/85 font-normal text-sm">Portfolio Intelligence</span>
          {s.finance_config_missing && (
            <span className="text-xs font-medium text-brand-200/80 bg-brand-950/45 border border-brand-400/25 px-2 py-0.5 rounded-md">
              Finance config not set
            </span>
          )}
          {(s.critical_alert_count || 0) > 0 && (
            <span className="text-xs font-medium text-rose-200/95 bg-rose-950/55 border border-rose-400/35 px-2 py-0.5 rounded-md flex items-center gap-1">
              <AlertTriangle className="w-2.5 h-2.5" strokeWidth={1.75} />
              {s.critical_alert_count} critical
            </span>
          )}
          {(s.warning_alert_count || 0) > 0 && (
            <span className="text-xs font-medium text-brand-200/85 bg-brand-950/45 border border-brand-400/25 px-2 py-0.5 rounded-md">
              {s.warning_alert_count} warning
            </span>
          )}
        </div>
        <Link
          to="/portfolio-intelligence"
          className="inline-flex items-center gap-1 text-xs text-brand-300/85 hover:text-brand-100 border border-brand-400/25 hover:bg-brand-950/40 rounded-full px-3 py-1 transition-colors"
        >
          Open Risk Command Center
          <ArrowUpRight className="w-3 h-3" strokeWidth={1.5} />
        </Link>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/[0.04]">
        <SummaryStat
          icon={Activity}
          label="Health"
          value={s.portfolio_health_score ?? '—'}
          sub={s.portfolio_status || '—'}
          tone={HEALTH_TONE(s.portfolio_health_score)}
        />
        <SummaryStat
          icon={Droplets}
          label="LCR · 14d"
          value={lcrLabel}
          sub={s.liquidity_coverage_status || '—'}
          tone={LCR_TONE(s.liquidity_coverage_ratio_14d)}
        />
        <SummaryStat
          icon={Clock}
          label="Compression"
          value={compressionLabel}
          sub={s.days_until_liquidity_compression == null ? 'None within 180d'
              : s.days_until_liquidity_compression < 21 ? 'Critical window'
              : 'Outside critical window'}
          tone={s.days_until_liquidity_compression == null
                  ? 'text-stone-300'
                  : s.days_until_liquidity_compression < 7
                    ? 'text-rose-300/90'
                    : s.days_until_liquidity_compression < 21
                      ? 'text-brand-200/85'
                      : 'text-emerald-300/85'}
        />
        <SummaryStat
          icon={ShieldAlert}
          label="Reserve gap"
          value={fmt$(reserveGap)}
          sub={reserveGap > 0
                  ? `Target ${fmt$(s.recommended_emergency_reserve)}`
                  : 'On target'}
          tone={reserveGap > 0 ? 'text-rose-300/90' : 'text-emerald-300/85'}
        />
      </div>
    </div>
  );
}

function SummaryStat({ icon: Icon, label, value, sub, tone }) {
  return (
    <div className="bg-zinc-950/80 px-3 py-2.5">
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-stone-500 font-normal mb-1">
        <Icon className="w-2.5 h-2.5 shrink-0" strokeWidth={1.25} />
        <span>{label}</span>
      </div>
      <p className={`text-xl font-extralight leading-none tabular-nums ${tone || 'text-white/90'}`}>{value}</p>
      <p className="text-xs text-stone-500/85 font-normal mt-1 leading-tight truncate">{sub}</p>
    </div>
  );
}



/**
 * The real Trips page for a risk row.
 *
 * This used to be a bespoke mini flight card rendered from the risk
 * SNAPSHOT (`a.flight_slices`) — a second, thinner rendering of a trip that
 * drifted from what Trips actually shows and could only ever display what the
 * snapshot happened to carry. Risk Review is where someone decides what to do
 * about a trip, so it should show the same page they would act on anywhere
 * else, not a summary of it (founder, 2026-09-18).
 *
 * The snapshot has no booking row, so the real one is fetched by id through
 * FlightBooking.fetchFull — the same endpoint the Trips list uses to hydrate
 * a slim row.
 */
function FlightInfoModal({ a, onClose, currentUser }) {
  const [trip, setTrip] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!a?.booking_id) { setError('This risk row has no booking on file.'); return undefined; }
    FlightBooking.fetchFull([a.booking_id])
      .then((rows) => {
        if (cancelled) return;
        if (!rows || rows.length === 0) { setError('That booking could not be loaded.'); return; }
        setTrip(rows);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[RiskReview] could not load the trip:', err?.message || err);
        setError('That booking could not be loaded.');
      });
    return () => { cancelled = true; };
  }, [a?.booking_id]);

  if (error) {
    return createPortal(
      <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={onClose}>
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
        <div className="relative z-10 bg-[#0f0f0f] border border-white/10 rounded-3xl px-6 py-5 max-w-md" onClick={(e) => e.stopPropagation()}>
          <p className="text-white text-base mb-1">{error}</p>
          <p className="text-sm text-white/60 font-normal">{a.route}{a.booking_reference ? ` · ${a.booking_reference}` : ''}</p>
          <button type="button" onClick={onClose} className="mt-4 h-9 px-4 rounded-full bg-white/10 hover:bg-white/15 text-white text-sm font-normal transition-colors">
            Back to Risk Review
          </button>
        </div>
      </div>,
      document.body,
    );
  }

  if (!trip) {
    return createPortal(
      <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={onClose}>
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
        <div className="relative z-10 flex items-center gap-3 text-white/70">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm font-normal">Opening {a.route}…</span>
        </div>
      </div>,
      document.body,
    );
  }

  return (
    <FlightDetailsModal
      trip={trip}
      isOpen
      onClose={onClose}
      onUpdate={onClose}
      currentUser={currentUser}
      backLabel="Back to Risk Review"
    />
  );
}


/**
 * PaymentScheduleModal — reuses the exact same <PaymentScheduleDisplay>
 * component rendered on the Trips page.  When a booking has no stored
 * payment_schedule object, we build the same `fallbackSchedule` object that
 * FlightDetailsModal builds so the display component can compute the
 * schedule from booking date + departure date + total, rather than showing
 * an empty state.
 */
function PaymentScheduleModal({ a, onClose }) {
  const isDeferaZero    = a.payment_method === 'defera_zero';
  const isDeferaCapital = a.payment_method === 'defera_capital';
  const isStripeInstallmentPlan = isDeferaZero || isDeferaCapital;

  /**
   * For Defera Zero/Capital trips, `a.total_amount` is Stripe-sourced when live data
   * is available (server `effectiveTotalAmount` ← subscription metadata), while
   * `a.grand_total_with_hold` is the raw booking column — which historically drifted
   * upward (AdminEditModal recompute double-added fees; C1NT4Q showed a $3,526.86
   * total against a $2,898.93 Stripe plan). Prefer the Stripe-truth column for
   * subscription trips so a drifted row can't inflate the schedule's grand total.
   * Non-subscription trips keep the gtwh-first convention (their billed total).
   */
  const totalWithFee = isStripeInstallmentPlan
    ? (Number(a.total_amount) || a.grand_total_with_hold || 0)
    : (a.grand_total_with_hold || a.total_amount || 0);
  const hasComputableSchedule = !!a.first_departure_date && totalWithFee > 0;

  // Match the fallbackSchedule object pattern used by FlightDetailsModal so
  // PaymentScheduleDisplay can re-derive the schedule when nothing is stored.
  // DC trips fall back to the lowest current DC tier (5%); the calculator will
  // snap up to a higher band if the lead time disallows 5%. Slice Pay keeps 20%.
  const fallbackSchedule = {
    deposit_percentage: isDeferaZero ? 0 : (isDeferaCapital ? 5 : 20),
    payment_plan: isStripeInstallmentPlan
      ? (a.payment_frequency || 'weekly')
      : 'weekly',
    booking_date: a.created_at,
    departure_date: a.first_departure_date,
  };

  const scheduleProp = (a.payment_schedule && typeof a.payment_schedule === 'object')
    ? a.payment_schedule
    : fallbackSchedule;

  const currentFlightData = {
    slices: [],
    payment_schedule: a.payment_schedule || null,
    _payment_method: a.payment_method,
    _payment_frequency: a.payment_frequency || null,
    /** Per-booking processor snapshot (surfaced by /api/admin/risk-review).
     *  Without it, PaymentScheduleDisplay routes NMI-track DC trips to the
     *  Stripe schedule endpoint, which 404s ("Defera Capital trip not found")
     *  because NMI bookings have no Stripe subscription. */
    _payment_processor: a.payment_processor || null,
  };
  const currentGrandTotalCents = Math.round(totalWithFee * 100);

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 py-6" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.18 }}
        className="relative z-10 w-full max-w-2xl bg-[#0f0f0f] border border-white/10 rounded-3xl shadow-2xl flex flex-col max-h-[90vh]"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/[0.07] shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <Receipt className="w-5 h-5 text-emerald-300/70 shrink-0" strokeWidth={1.5} />
            <span className="text-lg font-light text-white tracking-tight">Payment Schedule</span>
            <span className="text-white/40 text-sm font-normal truncate">— {a.route}</span>
          </div>
          <button type="button" onClick={onClose} className="text-white/30 hover:text-white/70 transition-colors shrink-0">
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 overflow-y-auto flex-1 min-h-0">
          {!hasComputableSchedule ? (
            <div className="flex items-center gap-3 bg-zinc-900/50 border border-white/[0.06] rounded-xl p-4 text-white/60 text-base font-normal">
              <Info className="w-5 h-5 shrink-0" />
              Not enough data to compute a payment schedule for this booking.
            </div>
          ) : (
            <PaymentScheduleDisplay
              paymentSchedule={scheduleProp}
              totalWithFee={totalWithFee}
              tripId={a.booking_id}
              bookingDate={a.created_at}
              departureDate={a.first_departure_date}
              tripReference={a.trip_reference || a.booking_reference || ''}
              userName={(a.passengers_detail?.[0]?.name) || 'Customer'}
              route={a.route}
              flightCreditCents={0}
              changeFlightHistory={a.change_flight_history || []}
              archivedFlightData={a.archived_flight_data || null}
              currentFlightData={currentFlightData}
              currentGrandTotalCents={currentGrandTotalCents}
              isDeferaZero={isDeferaZero}
              isDeferaCapital={isDeferaCapital}
              showActions={false}
            />
          )}
        </div>
      </motion.div>
    </div>,
    document.body
  );
}

// ─── Price Insights Modal ──────────────────────────────────────────────────────
function PriceModal({ a, onClose }) {
  const [priceData, setPriceData] = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  // Prices the team found by hand (migration 206). Read on their own, so they
  // still show when the live search is down or fails.
  const [manualPrices, setManualPrices] = useState([]);

  useEffect(() => {
    if (!a.booking_id || a.source === 'reserve') return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { supabase } = await import('@/lib/supabase');
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) return;
        const resp = await fetch(`/api/admin/booking-manual-prices?booking_id=${encodeURIComponent(a.booking_id)}`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        const data = await resp.json().catch(() => ({}));
        if (!cancelled && resp.ok && Array.isArray(data?.manual_prices)) setManualPrices(data.manual_prices);
      } catch { /* the live price check still shows */ }
    })();
    return () => { cancelled = true; };
  }, [a.booking_id, a.source]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { supabase } = await import('@/lib/supabase');
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) throw new Error('Not authenticated');
        const resp = await fetch('/api/admin/risk-review/price-check', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
          body: JSON.stringify({
            booking_id: a.booking_id,
            source: a.source,
            // Last-resort fallback so the modal can always show "Booked at"
            // even when the booking row has empty total/grand_total columns.
            client_booked_total: a.total_amount ?? a.grand_total_with_hold ?? null,
          }),
        });
        if (!resp.ok) throw new Error((await resp.text()) || `HTTP ${resp.status}`);
        const data = await resp.json();
        if (!cancelled) setPriceData(data);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to fetch price data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [a.booking_id, a.source]);

  const pi = priceData?.price_insights;
  const historyData = pi?.price_history || [];
  const levelColor = pi?.price_level
    ? pi.price_level.toLowerCase() === 'low'     ? 'text-emerald-400'
    : pi.price_level.toLowerCase() === 'typical' ? 'text-brand-300'
    : 'text-rose-400'
    : 'text-stone-400';

  // Booked vs current — verdict-driven comparison (mirrors Trips Price Check)
  const bookingRef = priceData?.booking_reference || a.booking_reference || a.trip_reference || null;
  const verdict = priceData?.verdict || null;
  const verdictTone =
    verdict === 'OK_TO_BOOK' ? {
      pill:   'bg-emerald-950/55 border-emerald-400/30 text-emerald-100',
      box:    'bg-emerald-950/30 border-emerald-400/20',
      accent: 'text-emerald-300',
      label:  'OK to book',
      copy:   'Current price is at or below the booked price — locking in this fare today is safe.',
      Icon:   CheckCircle2,
    }
    : verdict === 'CAUTION' ? {
      pill:   'bg-rose-950/55 border-rose-400/30 text-rose-100',
      box:    'bg-rose-950/30 border-rose-400/20',
      accent: 'text-rose-300',
      label:  'Caution — fare increased',
      copy:   'Current price is higher than what we collected. Ticketing now eats into margin; weigh against default risk.',
      Icon:   AlertTriangle,
    }
    : verdict === 'NEUTRAL' ? {
      pill:   'bg-stone-900/60 border-white/10 text-stone-200',
      box:    'bg-zinc-900/40 border-white/[0.06]',
      accent: 'text-stone-300',
      label:  'About the same',
      copy:   'Current price is within ~$10 of what was booked. No fare urgency either way.',
      Icon:   Info,
    }
    : null;

  // Forward-looking signal: where is this fare likely to go FROM HERE? The
  // verdict above is backward-looking (booked vs now); this answers "if I
  // wait, will it get cheaper or more expensive?" so the admin has both the
  // historical delta and the expected direction in one view.
  const forecast = forecastFare(pi);

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 py-6" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.18 }}
        className="relative z-10 w-full max-w-lg bg-[#0f0f0f] border border-white/10 rounded-2xl shadow-2xl flex flex-col max-h-[90vh]"
        onClick={e => e.stopPropagation()}
      >
        {/* Header — sticky */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.07] shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <BarChart2 className="w-4 h-4 text-brand-300/60 shrink-0" strokeWidth={1.5} />
            <span className="text-base font-normal text-white/90 tracking-wide">Price Insights</span>
            <span className="text-stone-500 text-sm font-normal truncate">— {a.route}</span>
            {bookingRef && (
              <span className="hidden sm:inline-flex items-center gap-1 ml-1 text-xs text-stone-500 font-normal">
                <span className="text-[10px] uppercase tracking-[0.12em] text-stone-600">Ref</span>
                <span className="font-mono text-stone-300 select-all">{bookingRef}</span>
              </span>
            )}
          </div>
          <button type="button" onClick={onClose} className="text-stone-500 hover:text-stone-200 transition-colors shrink-0">
            <XIcon className="w-4 h-4" />
          </button>
        </div>

        {/* Body — scrollable */}
        <div className="p-4 space-y-4 overflow-y-auto flex-1 min-h-0">
          {loading && (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <RefreshCw className="w-6 h-6 text-brand-200/25 animate-spin" />
              <p className="text-stone-500 text-sm font-normal">Fetching live prices via SerpAPI…</p>
            </div>
          )}

          {error && (
            <div className="bg-rose-950/30 border border-rose-400/20 rounded-xl p-4 text-rose-300/80 text-base font-normal">
              {error}
            </div>
          )}

          {!loading && !error && priceData && (
            <>
              {/* Booked vs Now — Trips-style price check, the primary signal
                  for "is it OK to ticket today?". Falls back gracefully when
                  we couldn't extract a booked price or match the live fare. */}
              {(priceData.booked_base_display || priceData.matched_current_display || priceData.current_price_display) && (
                <div className={`rounded-xl border p-3 sm:p-4 ${verdictTone ? verdictTone.box : 'bg-zinc-900/40 border-white/[0.06]'}`}>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-[10px] text-stone-500 font-normal uppercase tracking-[0.18em] mb-1">Booked at</p>
                      <p className="text-2xl font-light text-white tracking-tight tabular-nums">
                        {priceData.booked_base_display ?? '—'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] text-stone-500 font-normal uppercase tracking-[0.18em] mb-1">
                        {priceData.matched ? 'Now (same flight)' : 'Now (lowest in market)'}
                      </p>
                      <p className="text-2xl font-light text-white tracking-tight tabular-nums">
                        {priceData.matched_current_display
                          ?? priceData.reference_current_display
                          ?? priceData.current_price_display
                          ?? '—'}
                      </p>
                    </div>
                  </div>

                  {(priceData.passenger_count || priceData.cabin_class) && (
                    <p className="mt-2 text-[10px] text-stone-500 font-normal tracking-wide">
                      {priceData.passenger_count
                        ? `${priceData.passenger_count} ${priceData.passenger_count === 1 ? 'passenger' : 'passengers'}`
                        : ''}
                      {priceData.passenger_count && priceData.cabin_class ? ' · ' : ''}
                      {priceData.cabin_class
                        ? priceData.cabin_class.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
                        : ''}
                      {priceData.return_date ? ' · roundtrip' : ' · one-way'}
                    </p>
                  )}

                  {/* Cabin-mismatch advisory — only renders when SerpAPI
                      returned matches for the same airline+flight but in a
                      different cabin than we booked, so the matcher refused
                      them. Keeps admins from misreading the "Now" figure. */}
                  {priceData.matched_current_display == null && priceData.stored_fare_brand && (
                    <p className="mt-2 text-[10px] text-brand-300/80 font-normal leading-relaxed">
                      Booked fare: {priceData.stored_fare_brand}. Couldn&apos;t quote that exact fare tier today
                      {priceData.reference_current_display ? ' — market low shown for direction only' : ''}.
                    </p>
                  )}
                  {priceData.matched_current_display == null && priceData.cabin_class && priceData.cabin_class !== 'economy' && !priceData.stored_fare_brand && (
                    <p className="mt-2 text-[10px] text-brand-300/80 font-normal leading-relaxed">
                      No {priceData.cabin_class.replace(/_/g, ' ')} availability found for this exact flight today.
                      Showing the cheapest fare on this route as a directional proxy only.
                    </p>
                  )}
                  {priceData.matched_cabin_actual && priceData.cabin_class
                    && !priceData.matched_cabin_actual.toLowerCase().includes(priceData.cabin_class.replace(/_/g, ' ')) && (
                    <p className="mt-2 text-[10px] text-brand-300/80 font-normal leading-relaxed">
                      Quoted cabin from live search: {priceData.matched_cabin_actual}
                    </p>
                  )}

                  {priceData.delta_display != null && verdictTone && (
                    <div className="mt-3 pt-3 border-t border-white/[0.06] flex items-center justify-between gap-3">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border ${verdictTone.pill}`}>
                        <verdictTone.Icon className="w-3 h-3 shrink-0" strokeWidth={1.5} />
                        {verdictTone.label}
                      </span>
                      <span className={`font-mono tabular-nums text-sm ${verdictTone.accent}`}>
                        {priceData.delta_display}
                      </span>
                    </div>
                  )}

                  {verdictTone?.copy && (
                    <p className="mt-2 text-[13px] text-stone-400 font-normal leading-relaxed">{verdictTone.copy}</p>
                  )}

                  {!verdictTone && priceData.booked_base_display && !priceData.matched_current_display && (
                    <p className="mt-2 text-[13px] text-stone-400 font-normal leading-relaxed">
                      Couldn&apos;t match the exact same airline + flight number in current results.
                      Comparison below uses the lowest fare on this route as a proxy.
                    </p>
                  )}

                  {!priceData.booked_base_display && (
                    <p className="mt-2 text-[13px] text-stone-400 font-normal leading-relaxed">
                      No booked price found in flight_data — showing market price only.
                    </p>
                  )}
                </div>
              )}

              {/* Fare forecast — will the price go up or down from here? */}
              {forecast && (
                <div className={`rounded-xl border p-3 sm:p-4 ${forecast.tone.box}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <p className="text-[10px] text-stone-500 font-normal uppercase tracking-[0.18em]">Fare forecast</p>
                    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border ${forecast.tone.pill}`}>
                      <forecast.Icon className="w-3 h-3 shrink-0" strokeWidth={1.5} />
                      {forecast.label}
                    </span>
                  </div>
                  <p className="mt-2 text-[13px] text-stone-400 font-normal leading-relaxed">{forecast.copy}</p>
                </div>
              )}

              {/* Route + market level */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-stone-500 font-normal uppercase tracking-widest mb-0.5">Market level</p>
                  {pi?.price_level ? (
                    <p className="text-sm font-normal">
                      Prices are currently <span className={`font-medium ${levelColor}`}>{pi.price_level.toLowerCase()}</span>
                    </p>
                  ) : (
                    <p className="text-sm font-normal text-stone-500">—</p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-xs text-stone-500 font-normal uppercase tracking-widest mb-0.5">Route</p>
                  <p className="text-base text-white/90 font-normal">{priceData.origin} → {priceData.destination}</p>
                  <p className="text-xs text-stone-500 font-normal">{priceData.outbound_date}{priceData.return_date ? ` · return ${priceData.return_date}` : ''}</p>
                </div>
              </div>

              {/* Price range bar */}
              {pi?.lowest_price && pi?.typical_price_range?.length === 2 && (
                <div className="space-y-1.5">
                  <div className="relative h-2 rounded-full overflow-hidden bg-gradient-to-r from-emerald-500 via-brand-500 to-rose-500">
                    {(() => {
                      const [min, max] = pi.typical_price_range;
                      const pos = Math.min(Math.max(((pi.lowest_price - min) / (max - min)) * 100, 0), 100);
                      return (
                        <div
                          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3.5 h-3.5 bg-white border-2 border-zinc-900 rounded-full shadow-lg z-10"
                          style={{ left: `${pos}%` }}
                        >
                          <div className="absolute -top-7 left-1/2 -translate-x-1/2 whitespace-nowrap">
                            <div className="bg-brand-400 text-zinc-950 text-xs px-1.5 py-0.5 rounded font-medium">
                              ${pi.lowest_price.toLocaleString()}
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                  <div className="flex justify-between text-xs text-stone-500 font-normal">
                    <span>${pi.typical_price_range[0].toLocaleString()}</span>
                    <span className="text-stone-600 text-xs">typical range</span>
                    <span>${pi.typical_price_range[1].toLocaleString()}</span>
                  </div>
                </div>
              )}

              {/* Chart */}
              {historyData.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs text-stone-500 font-normal uppercase tracking-widest">Price history</p>
                  <div className="h-40">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={historyData}>
                        <defs>
                          <linearGradient id="priceRiskGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%"  stopColor="#0A1233" stopOpacity={0.35} />
                            <stop offset="95%" stopColor="#0A1233" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <XAxis dataKey="date" tick={{ fill: '#6b7280', fontSize: 10 }} stroke="#27272a" />
                        <YAxis
                          tick={{ fill: '#6b7280', fontSize: 10 }}
                          stroke="#27272a"
                          tickFormatter={v => `$${v}`}
                          domain={([min, max]) => [Math.max(0, Math.floor(min * 0.9)), Math.ceil(max * 1.1)]}
                        />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a', borderRadius: '8px', color: '#fff' }}
                          formatter={v => [`$${Number(v).toLocaleString()}`, 'Price']}
                          labelStyle={{ color: '#9ca3af', fontSize: 11 }}
                        />
                        <Area type="monotone" dataKey="price" stroke="#0A1233" strokeWidth={2} fill="url(#priceRiskGradient)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {!pi && !priceData.current_price_display && (
                <p className="text-stone-400 text-base font-normal text-center py-4">No price data returned for this route.</p>
              )}

              <p className="text-xs text-stone-600 font-normal text-right">Powered by Google Flights via SerpAPI</p>
            </>
          )}

          {/* Prices the team found by hand (Downtown Travel, the airline, a
              manual search): one line each, newest first, shown whether or
              not the live search worked. */}
          {!loading && manualPrices.length > 0 && (
            <div className="rounded-xl border border-stone-700/60 bg-stone-900/40 p-3 sm:p-4" data-testid="manual-prices">
              <p className="text-[10px] text-stone-500 font-normal uppercase tracking-[0.18em] mb-2">Manual searches</p>
              <ul className="space-y-1.5">
                {manualPrices.map((m) => (
                  <li key={m.id} className="text-[13px] font-normal">
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="text-stone-200">{m.line}</span>
                      <span className="text-stone-500 text-[12px] shrink-0">
                        {new Date(m.checked_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                    </div>
                    {(m.note || !m.same_flights) && (
                      <p className="text-stone-400 text-[12px]">
                        {m.note || ''}{m.note && !m.same_flights ? ' · ' : ''}{!m.same_flights ? 'other flights' : ''}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </motion.div>
    </div>,
    document.body
  );
}

// ─── Trip table ──────────────────────────────────────────────────────────────
// The plain chart the founder asked for (2026-10-02, "I rather have a simple
// chart like this. Keep the buttons."): one row per trip, the five per-trip
// buttons as icons on the row, and the full old card one click away.

/** Departs and Paid are the two rankings (RankToggle); clicking either
 *  header switches to it. The rest are plain columns — nothing else may
 *  reorder the list (founder: "always ranked by soonest departure"). */
const TRIP_COLUMNS = [
  { key: 'departs',  label: 'Departs',      sort: 'departure', dir: 'asc',  align: 'left',  title: 'Rank by soonest departure' },
  { key: 'customer', label: 'Customer',     sort: null,        dir: null,   align: 'left' },
  { key: 'ref',      label: 'Ref',          sort: null,        dir: null,   align: 'left' },
  { key: 'paid',     label: 'Paid',         sort: 'pct_paid',  dir: 'desc', align: 'right', title: 'Rank by most paid (10% bands, soonest departure within each)' },
  { key: 'base',     label: 'Base',         sort: null,        dir: null,   align: 'right', title: 'Airline ticket cost when booked' },
  { key: 'total',    label: 'Total',        sort: null,        dir: null,   align: 'right', title: 'Trip total the customer pays, fees included' },
  { key: 'now',      label: 'Base now',     sort: null,        dir: null,   align: 'right', title: "Today's price for the same flights, from the last Refresh + AI" },
  { key: 'profit',   label: 'Net profit',   sort: null,        dir: null,   align: 'right', title: "What Defera keeps: the trip, less the ticket at today's fare (or its actual cost), card fees, miles and any backer's cut" },
  { key: 'last',     label: 'Last payment', sort: null,        dir: null,   align: 'left',  title: "The customer's final scheduled charge" },
];
/** Chevron + the columns above + the button cell. */
const TRIP_TABLE_SPAN = TRIP_COLUMNS.length + 2;

const TD = 'px-2.5 py-2.5 border-t border-white/[0.05] align-middle';

function fmtUsdCents(n) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(Number(n || 0));
}

/** Same tier the old card's rail used: the most concerning of the risk level,
 *  the AI verdict, and a portfolio action that overrules the AI. */
function rowRiskTier(a) {
  const aiOk = a.ai_assessment?.ok !== false && !!a.ai_assessment?.verdict;
  const aiVerdict = aiOk ? a.ai_assessment.verdict : null;
  const finalAction = a.booking_priority?.final_action || null;
  const finalIsOverride = aiOk && finalAction && ((aiVerdict === 'BOOK_NOW') !== (finalAction === 'BOOK_NOW'));
  return effectiveRiskTier({
    riskLevel: a.risk_level,
    aiVerdict,
    finalAction: finalIsOverride ? finalAction : null,
  });
}

function RiskTripTable({ rows, sortBy, onSort, focusedBookingId, onDeepDive, currentUser }) {
  const isMobile = useIsMobile();
  const now = new Date();
  const groups = groupTrips(rows, sortBy);
  const rowProps = (a) => ({
    key: `${a.source}-${a.booking_id}`,
    a,
    now,
    defaultExpanded: !!focusedBookingId && a.booking_id === focusedBookingId,
    onDeepDive: () => onDeepDive(a),
    currentUser,
  });
  const groupCount = (g) => `${g.rows.length} trip${g.rows.length === 1 ? '' : 's'}`;

  // Phones: the same list as one panel of slim rows, no sideways scrolling.
  if (isMobile) {
    return (
      <div className={`${PANEL} overflow-hidden divide-y divide-white/[0.05]`}>
        {groups.map((g) => (
          <Fragment key={g.key}>
            {g.label && (
              <div className="px-3.5 pt-3 pb-1.5 text-xs font-medium uppercase tracking-[0.14em] text-brand-200/70">
                {g.label} <span className="normal-case tracking-normal font-normal text-stone-500">· {groupCount(g)}</span>
              </div>
            )}
            {g.rows.map((a) => {
              const { key, ...props } = rowProps(a);
              return <RiskTripRow key={key} layout="card" {...props} />;
            })}
          </Fragment>
        ))}
      </div>
    );
  }
  return (
    <div className={`${PANEL} overflow-x-auto`}>
      <table className="w-full min-w-[60rem] border-separate border-spacing-0 text-sm">
        <thead>
          <tr>
            <th scope="col" className="w-10 pl-3 pr-1 py-2.5">
              <span className="sr-only">Details</span>
            </th>
            {TRIP_COLUMNS.map((col) => {
              const active = col.sort && sortBy === col.sort;
              const ariaSort = active ? (col.dir === 'asc' ? 'ascending' : 'descending') : undefined;
              const SortIcon = col.dir === 'asc' ? ChevronUp : ChevronDown;
              return (
                <th
                  key={col.key}
                  scope="col"
                  aria-sort={ariaSort}
                  className={`px-2.5 py-2.5 whitespace-nowrap text-xs font-medium uppercase tracking-[0.14em] ${active ? 'text-stone-200' : 'text-stone-500'} ${col.align === 'right' ? 'text-right' : 'text-left'}`}
                >
                  {col.sort ? (
                    <button
                      type="button"
                      onClick={() => onSort(col.sort)}
                      title={col.title}
                      className={`inline-flex items-center gap-1 uppercase tracking-[0.14em] hover:text-stone-200 transition-colors ${col.align === 'right' ? 'flex-row-reverse' : ''}`}
                    >
                      {col.label}
                      <SortIcon className={`w-3 h-3 shrink-0 ${active ? 'opacity-100' : 'opacity-0'}`} aria-hidden />
                    </button>
                  ) : (
                    <span title={col.title}>{col.label}</span>
                  )}
                </th>
              );
            })}
            <th scope="col" className="px-2.5 py-2.5">
              <span className="sr-only">Trip buttons</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => (
            <Fragment key={g.key}>
              {g.label && (
                <tr>
                  <td
                    colSpan={TRIP_TABLE_SPAN}
                    className="px-3 pl-12 pt-4 pb-1.5 border-t border-white/[0.05] text-xs font-medium uppercase tracking-[0.14em] text-brand-200/70"
                  >
                    {g.label} <span className="normal-case tracking-normal font-normal text-stone-500">· {groupCount(g)}</span>
                  </td>
                </tr>
              )}
              {g.rows.map((a) => {
                const { key, ...props } = rowProps(a);
                return <RiskTripRow key={key} {...props} />;
              })}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const ROW_ICON_TONE = {
  brand:   'bg-brand-950/40 hover:bg-brand-900/50 text-brand-200/80 border-brand-400/20',
  emerald: 'bg-emerald-950/40 hover:bg-emerald-900/50 text-emerald-200/80 border-emerald-400/20',
  stone:   'bg-stone-900/60 hover:bg-stone-800/70 text-stone-300 border-white/10',
};

function RowIconButton({ label, icon: Icon, tone, onClick }) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      aria-label={label}
      title={label}
      className={`w-7 h-7 inline-flex items-center justify-center rounded-full border transition-colors ${ROW_ICON_TONE[tone] || ROW_ICON_TONE.stone}`}
    >
      <Icon className="w-3.5 h-3.5" strokeWidth={1.25} />
    </button>
  );
}

/** The five per-trip buttons for a phone: one row of icon + short label,
 *  at the top of an opened trip. Accessible names stay the full ones. */
function MobileTripButtons({ onDeepDive, onOpenFlight, onOpenSchedule, onOpenPrice, onOpenHistory }) {
  const items = [
    { label: 'Flight', name: 'Flight details', icon: Plane, tone: 'brand', onClick: onOpenFlight },
    { label: 'Schedule', name: 'Payment schedule', icon: Receipt, tone: 'emerald', onClick: onOpenSchedule },
    { label: 'Price', name: 'Price insights', icon: BarChart2, tone: 'brand', onClick: onOpenPrice },
    { label: 'History', name: 'Decision history', icon: ClipboardList, tone: 'stone', onClick: onOpenHistory },
    { label: 'AI', name: 'AI deep dive', icon: Brain, tone: 'brand', onClick: onDeepDive },
  ];
  return (
    <div className="grid grid-cols-5 gap-1.5 px-3 pt-3">
      {items.map(({ label, name, icon: Icon, tone, onClick }) => (
        <button
          key={label}
          type="button"
          onClick={onClick}
          aria-label={name}
          title={name}
          className={`flex flex-col items-center justify-center gap-1 rounded-xl border py-2 text-[11px] font-normal transition-colors ${ROW_ICON_TONE[tone]}`}
        >
          <Icon className="w-4 h-4" strokeWidth={1.25} />
          {label}
        </button>
      ))}
    </div>
  );
}

function RiskTripRow({ a, now, defaultExpanded, onDeepDive, currentUser, layout = 'table' }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [showFlight, setShowFlight] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [showPrice, setShowPrice] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const tier = rowRiskTier(a);
  const verdict = displayedTripAction(a);
  const depart = parseDisplayDate(a.first_departure_date);
  const daysOut = daysUntil(a.first_departure_date, now);
  const soon = daysOut != null && daysOut >= 0 && daysOut <= 14;
  const total = tripTotal(a);
  const paid = Math.max(0, Number(a.paid_to_date) || 0);
  const pct = paidPct(a);
  const base = bookedBase(a);
  const fare = baseNow(a);
  const profit = netProfit(a);
  const last = lastPayment(a);
  const issue = paymentIssue(a);
  const issueCopy = issue ? (PAYMENT_ISSUE_COPY[issue] || PAYMENT_ISSUE_COPY.past_due) : null;
  const ref = tripRef(a);
  const pax = Number(a.passenger_count) || (a.passengers_detail || []).length || 1;
  const notLive = a.paid_data_source && a.paid_data_source !== 'live';
  const railClass = `before:absolute before:left-0 before:top-2 before:bottom-2 before:w-[2px] before:rounded-full ${RISK_RAIL[tier] || ''}`;
  const riskTitle = `Risk ${a.risk_score ?? '—'}${verdict ? ` · ${ACTION_LABEL[verdict] || verdict}` : ''}`;

  // Selecting text (copying a ref or a name) must not fold the row.
  const toggle = () => {
    try { if (window.getSelection()?.toString()) return; } catch { /* ignore */ }
    setExpanded((v) => !v);
  };

  const fareTone = !fare ? 'text-stone-600'
    : fare.direction === 'up' ? (fare.current > total ? 'text-rose-300 font-medium' : 'text-rose-200/85')
      : fare.direction === 'down' ? 'text-emerald-300'
        : 'text-stone-300';
  const fareTitle = fare ? [
    `Booked ${fmt$(base ?? 0)} → now ${fmt$(fare.current)}`
      + (fare.direction === 'flat' ? ' (unchanged)'
        : ` (${fare.deltaCents >= 0 ? '+' : '−'}${fmt$(Math.abs(fare.deltaCents) / 100)}, ${fare.deltaPct >= 0 ? '+' : '−'}${Math.abs(fare.deltaPct).toFixed(0)}%)`),
    fare.current > total ? `More than the whole ${fmtUsdCents(total)} trip total` : null,
    fare.exact ? 'Exact flight' : 'Cheapest comparable fare (exact flight not listed)',
    fare.fetchedAt ? `Checked ${format(new Date(fare.fetchedAt), 'MMM d, h:mm a')}` : null,
  ].filter(Boolean).join('\n') : 'No live price yet — Refresh + AI checks today\'s fare for each trip';

  const chevron = (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
      aria-expanded={expanded}
      aria-label={expanded ? 'Hide trip details' : 'Show trip details and risk drivers'}
      className="w-6 h-6 inline-flex items-center justify-center rounded-full text-stone-500 hover:text-stone-200 hover:bg-white/[0.06] transition-colors"
    >
      <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
    </button>
  );
  const departs = (
    <span
      className={soon ? 'text-white font-medium' : 'text-stone-200'}
      title={depart ? `${format(depart, 'EEE, MMM d yyyy')}${daysOut != null ? ` · ${daysLabel(daysOut)}` : ''}` : undefined}
    >
      {shortDate(a.first_departure_date, now) || '—'}
    </span>
  );
  const customer = (
    <span className="inline-flex min-w-0 items-center gap-1.5">
      <span className="text-stone-100 truncate" title={a.user_email && a.user_email !== '—' ? a.user_email : undefined}>
        {customerName(a)}
      </span>
      <VipBadge email={a.user_email && a.user_email !== '—' ? a.user_email : null} />
    </span>
  );
  const paidCell = (
    <span
      className={pct >= 70 ? 'text-stone-100' : 'text-stone-300'}
      title={`${fmtUsdCents(paid)} of ${fmtUsdCents(total)} paid${notLive ? ` · ${a.paid_data_source === 'deposit_floor' ? 'deposit-based lower bound (Stripe unreachable)' : 'last-known value (Stripe unreachable)'}` : ''}`}
    >
      {a.paid_data_source === 'deposit_floor' ? '≥' : ''}{fmtPct(pct)}
    </span>
  );
  const fareCell = (
    <span className={fareTone} title={fareTitle}>
      {fare ? (
        <>
          {fmt$(fare.current)}
          {fare.direction !== 'flat' && (
            <span className="ml-1 text-[10px]" aria-label={fare.direction === 'up' ? 'up since booking' : 'down since booking'}>
              {fare.direction === 'up' ? '▲' : '▼'}
            </span>
          )}
        </>
      ) : '—'}
    </span>
  );
  const profitWhole = profit ? Math.round(profit.amount) : 0;
  const profitCell = (
    <span
      className={!profit ? 'text-stone-600'
        : profitWhole > 0 ? 'text-emerald-300'
          : profitWhole < 0 ? 'text-rose-300'
            : 'text-stone-300'}
      title={netProfitLines(a).join('\n')}
    >
      {profit ? fmtNetProfit(profit.amount) : '—'}
    </span>
  );
  const lastCell = (
    <span className="inline-flex items-center gap-1.5">
      <span className={last.label ? 'text-stone-200' : 'text-stone-600'}>{last.label || '—'}</span>
      {last.basis === 'paid' && (
        <span className="text-[11px] text-emerald-300/85">paid off</span>
      )}
      {last.basis === 'unscheduled' && (
        <span className="text-[11px] text-brand-200/70" title="No more charges are scheduled, but a balance remains">nothing scheduled</span>
      )}
      {issueCopy && (
        <span className="text-[11px] font-medium text-rose-200 bg-rose-950/50 border border-rose-400/25 rounded-md px-1.5 py-px" title={issueCopy.title}>
          {issueCopy.label}
        </span>
      )}
    </span>
  );
  const buttons = (
    <div className="inline-flex items-center gap-1">
      <RowIconButton label="Flight details" icon={Plane} tone="brand" onClick={() => setShowFlight(true)} />
      <RowIconButton label="Payment schedule" icon={Receipt} tone="emerald" onClick={() => setShowSchedule(true)} />
      <RowIconButton label="Price insights" icon={BarChart2} tone="brand" onClick={() => setShowPrice(true)} />
      <RowIconButton label="Decision history" icon={ClipboardList} tone="stone" onClick={() => setShowHistory(true)} />
      <RowIconButton label="AI deep dive" icon={Brain} tone="brand" onClick={onDeepDive} />
    </div>
  );
  const opens = {
    onDeepDive,
    onOpenFlight: () => setShowFlight(true),
    onOpenSchedule: () => setShowSchedule(true),
    onOpenPrice: () => setShowPrice(true),
    onOpenHistory: () => setShowHistory(true),
  };
  const panel = expanded ? <TripDetailPanel a={a} {...opens} /> : null;
  // Always outside the clickable row: React bubbles events through portals
  // along the component tree, so a modal rendered inside the row would fold
  // it when its backdrop is clicked to close.
  const modals = (
    <>
      {showFlight && <FlightInfoModal a={a} onClose={() => setShowFlight(false)} currentUser={currentUser} />}
      {showSchedule && <PaymentScheduleModal a={a} onClose={() => setShowSchedule(false)} />}
      {showPrice && <PriceModal a={a} onClose={() => setShowPrice(false)} />}
      {showHistory && (
        <DecisionHistoryDialog
          open
          onClose={() => setShowHistory(false)}
          bookingId={a.booking_id}
          bookingReference={ref}
          route={a.route}
        />
      )}
    </>
  );

  // Phones: three slim lines that read like the desktop row. Departure sits
  // on the left edge and % paid on the right edge of the first line, so the
  // two rankings scan straight down the list; Base · Total · Now spread on
  // the third. The five buttons open with the trip (MobileTripButtons).
  if (layout === 'card') {
    const otherYear = depart && depart.getFullYear() !== now.getFullYear();
    return (
      <>
        <div data-booking-id={a.booking_id} className={`relative ${railClass}`}>
          <div onClick={toggle} className="px-3.5 py-2.5 cursor-pointer transition-colors active:bg-white/[0.03]" title={riskTitle}>
            <div className="flex items-center gap-2.5">
              <span className={`w-[3.75rem] shrink-0 text-sm tabular-nums whitespace-nowrap ${soon ? 'text-white font-medium' : 'text-stone-200'}`}>
                {depart ? format(depart, 'MMM d') : '—'}
                {otherYear && <span className="ml-0.5 text-[10px] font-normal text-stone-500">’{format(depart, 'yy')}</span>}
              </span>
              <span className="flex-1 min-w-0 flex items-baseline gap-1.5 text-sm">
                {customer}
                {pax > 1 && <span className="text-stone-500 text-xs whitespace-nowrap">· {pax}</span>}
              </span>
              <span className="shrink-0 text-sm tabular-nums">{paidCell}</span>
              <span className="shrink-0 -my-1 -mr-1.5">{chevron}</span>
            </div>
            {/* Net profit sits under % paid: four labelled figures don't fit
                one 375px line (30 of 112 trips overflowed). On a row with a
                payment badge the "Last payment" label gives way first, never
                the date, the badge or the profit. */}
            <div className="mt-1 flex items-center gap-2.5 text-xs">
              <span className="w-[3.75rem] shrink-0 font-mono text-stone-400">{ref || '—'}</span>
              <span className="flex-1 min-w-0 flex items-center gap-1.5 text-stone-500 whitespace-nowrap">
                <span className="truncate">Last payment</span>
                <span className="shrink-0">{lastCell}</span>
              </span>
              <span className="shrink-0 tabular-nums text-stone-500 whitespace-nowrap">Profit {profitCell}</span>
            </div>
            <div className="mt-1 flex items-center justify-between gap-2 text-xs tabular-nums text-stone-500 whitespace-nowrap">
              <span>Base <span className="text-stone-300">{base != null ? fmt$(base) : '—'}</span></span>
              <span>Total <span className="text-stone-300">{total > 0 ? fmtUsdCents(total) : '—'}</span></span>
              <span>Now {fareCell}</span>
            </div>
          </div>
          {expanded && (
            <div className="border-t border-white/[0.06] bg-white/[0.015]">
              <MobileTripButtons {...opens} />
              <TripDetailPanel a={a} {...opens} compact />
            </div>
          )}
        </div>
        {modals}
      </>
    );
  }

  return (
    <>
      <tr
        data-booking-id={a.booking_id}
        onClick={toggle}
        className="cursor-pointer transition-colors hover:bg-white/[0.025]"
      >
        <td className={`${TD} relative pl-3 pr-1 ${railClass}`} title={riskTitle}>
          {chevron}
        </td>
        <td className={`${TD} whitespace-nowrap`}>{departs}</td>
        <td className={`${TD} max-w-[16rem]`}>
          <div className="flex items-baseline gap-1.5 min-w-0">
            {customer}
            {pax > 1 && (
              <span className="text-stone-500 text-xs whitespace-nowrap">· {pax} pax</span>
            )}
          </div>
        </td>
        <td className={`${TD} whitespace-nowrap`}>
          <span className="font-mono text-[13px] text-stone-300">{ref || '—'}</span>
        </td>
        <td className={`${TD} text-right tabular-nums whitespace-nowrap`}>{paidCell}</td>
        <td className={`${TD} text-right tabular-nums whitespace-nowrap text-stone-300`}>
          {base != null ? fmt$(base) : <span className="text-stone-600">—</span>}
        </td>
        <td className={`${TD} text-right tabular-nums whitespace-nowrap text-stone-300`}>
          {total > 0 ? fmtUsdCents(total) : <span className="text-stone-600">—</span>}
        </td>
        <td className={`${TD} text-right tabular-nums whitespace-nowrap`}>{fareCell}</td>
        <td className={`${TD} text-right tabular-nums whitespace-nowrap`}>{profitCell}</td>
        <td className={`${TD} whitespace-nowrap`}>{lastCell}</td>
        <td className={`${TD} text-right whitespace-nowrap`} onClick={(e) => e.stopPropagation()}>
          {buttons}
        </td>
      </tr>

      {panel && (
        <tr>
          <td colSpan={TRIP_TABLE_SPAN} className="p-0 border-t border-white/[0.05] bg-white/[0.015]">
            {panel}
          </td>
        </tr>
      )}

      {modals}
    </>
  );
}

/**
 * TripDetailPanel — everything the old per-trip card showed, now opened from
 * a table row: verdict, paid progress, deadline/loss/Radar, live fare,
 * rationale, AI synthesis + fare trajectory, risk drivers, payment history,
 * customer behaviour, Stripe meta, and the labelled per-trip buttons. The
 * row owns the modals, so the row's icons and these buttons open the same ones.
 */
function TripDetailPanel({ a, onDeepDive, onOpenFlight, onOpenSchedule, onOpenPrice, onOpenHistory, compact = false }) {
  // Phones (compact) keep the long AI write-ups and drivers behind one tap,
  // like the old card's "Risk drivers" toggle; desktop shows them outright.
  const [showDeep, setShowDeep] = useState(!compact);
  const ActionIcon  = ACTION_ICON[a.recommendation?.action]  || Eye;
  const actionPill  = ACTION_PILL[a.recommendation?.action]  || ACTION_PILL.MONITOR;

  const paxSummary = (a.passengers_detail || []).slice(0, 2).map((p) => {
    const parts = [p.name || 'Passenger'];
    if (p.age != null) parts.push(`${p.age}y`);
    return parts.join(' · ');
  }).join(', ');
  const extraPax = (a.passengers_detail || []).length > 2
    ? ` +${(a.passengers_detail || []).length - 2}`
    : '';

  const finalAction = a.booking_priority?.final_action || null;

  // What's the AI's primary call? Resolve once so we can decide whether the
  // portfolio/final override actually disagrees and warrants a separate pill.
  const aiOk = a.ai_assessment?.ok !== false && !!a.ai_assessment?.verdict;
  const aiVerdict = aiOk ? a.ai_assessment.verdict : null;
  const aiConf = aiOk && a.ai_assessment.confidence != null
    ? Math.round(a.ai_assessment.confidence * 100)
    : null;
  const fareUrg = a.ai_assessment?.fare_trajectory?.fare_urgency || null;
  const fareBy  = a.ai_assessment?.fare_trajectory?.recommended_ticket_by_dtd;

  // Translate the AI's "ticket by N days before departure" into an actual
  // calendar date so admins don't have to do mental math. If the recommended
  // book-by date is already in the past relative to today, show "Book ASAP"
  // instead of a misleading historical date.
  const bookByInfo = (() => {
    if (!Number.isFinite(fareBy) || !a.first_departure_date) return null;
    const dep = new Date(a.first_departure_date);
    if (isNaN(dep)) return null;
    const bookByDate = new Date(dep.getTime() - fareBy * 86400000);
    if (isNaN(bookByDate)) return null;
    const daysFromNow = Math.ceil((bookByDate.getTime() - Date.now()) / 86400000);
    return { date: bookByDate, daysFromNow };
  })();

  // Override surfaces only when the portfolio layer actually overrules the
  // AI's "go / no-go" decision. AI BOOK_NOW + final BOOK_NOW → no override.
  // AI BOOK_NOW + final HOLD_FOR_LIQUIDITY → real override, surface it.
  // AI fails → fall back to deterministic recommendation as the primary.
  const aiSaysBook = aiVerdict === 'BOOK_NOW';
  const finalSaysBook = finalAction === 'BOOK_NOW';
  const finalIsOverride = aiOk && finalAction && (aiSaysBook !== finalSaysBook);

  return (
    <div>
      <div className="px-3 sm:px-4 pt-3 pb-3.5">

        {/* ── Header ──────────────────────────────────────────────── */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-2">
          {/* Left: route + meta */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 flex-wrap min-w-0">
              <Plane className="w-3.5 h-3.5 text-brand-200/40 shrink-0" strokeWidth={1.25} />
              <span className="text-white/95 font-normal text-base tracking-tight">{a.route}</span>
              {a.marketing_airline && (
                <span className="text-stone-500 text-sm font-normal">· {a.marketing_airline}</span>
              )}
              {a.payment_method === 'defera_capital' && (
                <span className="text-xs font-medium text-brand-200/80 bg-brand-950/50 border border-brand-400/20 px-2 py-0.5 rounded-md">Capital</span>
              )}
              {a.payment_method === 'defera_zero' && (
                <span className="text-xs font-medium text-emerald-200/80 bg-emerald-950/50 border border-emerald-400/20 px-2 py-0.5 rounded-md">Zero</span>
              )}
              {a.international && (
                <span className="text-xs font-normal text-stone-400 bg-zinc-900/60 border border-white/10 px-2 py-0.5 rounded-md">Intl</span>
              )}
              {a.subscription_status && a.subscription_status !== 'active' && (
                <span className={`text-xs font-medium px-2 py-0.5 rounded-md border ${
                  a.subscription_status === 'past_due'
                    ? 'text-rose-200/90 bg-rose-950/45 border-rose-400/25'
                    : a.subscription_status === 'canceled'
                      ? 'text-zinc-300 bg-zinc-900/50 border-zinc-500/20'
                      : 'text-brand-200/80 bg-brand-950/40 border-brand-400/20'
                }`}>
                  {a.subscription_status}
                </span>
              )}
              {a.last_payment_failed && (
                <span className="text-xs font-medium text-rose-200/90 bg-rose-950/45 border border-rose-400/25 px-2 py-0.5 rounded-md">Pmt failed</span>
              )}
              {/* Radar score lives in its dedicated StatCell below — keeping
                  it out of the header keeps the route line calmer and stops
                  it competing with the AI verdict on the right. */}
            </div>

            <div className="mt-1 flex items-center gap-1.5 text-xs text-stone-500 flex-wrap font-normal min-w-0">
              <UsersIcon className="w-3 h-3 text-stone-600 shrink-0" strokeWidth={1.25} />
              <span className="text-stone-400">{paxSummary || `${a.passenger_count} pax`}{extraPax}</span>
              {a.user_email && a.user_email !== '—' && (
                <>
                  <span className="text-stone-700">·</span>
                  <span className="text-stone-300 min-w-0 truncate select-all">{a.user_email}</span>
                </>
              )}
            </div>
          </div>

          {/* Right: single verdict — AI's call is the primary answer.
              Everything that used to be a separate pill (risk, fare, score,
              portfolio, final, priority) is collapsed into one muted context
              line beneath the verdict. The portfolio "Final" action only
              surfaces as a separate override pill when it actually contradicts
              the AI's go/no-go decision. */}
          <div className="flex flex-col items-start sm:items-end gap-1 sm:shrink-0 min-w-0">
            {aiOk && (() => {
              const AiIcon = ACTION_ICON[aiVerdict] || Eye;
              const aiPill = ACTION_PILL[aiVerdict] || ACTION_PILL.MONITOR;
              return (
                <span
                  className={`px-2.5 py-1 rounded-full text-sm font-medium border flex items-center gap-1.5 ${aiPill}`}
                  title={a.ai_assessment.synthesis || a.ai_assessment.booking_timing_plan || a.ai_assessment.fare_risk_note || ''}
                >
                  <AiIcon className="w-3 h-3 shrink-0" strokeWidth={1.75} />
                  {ACTION_LABEL[aiVerdict] || aiVerdict}
                  {aiConf != null && (
                    <span className="text-[10px] text-white/45 ml-0.5 tabular-nums">{aiConf}%</span>
                  )}
                </span>
              );
            })()}

            {/* AI failure → fall back to deterministic as primary */}
            {!aiOk && (
              <>
                <span
                  className={`px-2.5 py-1 rounded-full text-sm font-medium border flex items-center gap-1.5 ${actionPill}`}
                  title={`Deterministic fallback — ${a.recommendation?.rationale || ''}`}
                >
                  <ActionIcon className="w-3 h-3 shrink-0" strokeWidth={1.75} />
                  {ACTION_LABEL[a.recommendation?.action] || 'Monitor'}
                </span>
                <span
                  className="text-[10px] text-stone-500 font-normal flex items-center gap-1"
                  title={a.ai_assessment?.error || 'AI unavailable'}
                >
                  <Brain className="w-2.5 h-2.5 shrink-0" strokeWidth={1.25} />
                  AI unavailable
                </span>
              </>
            )}

            {/* Override pill — only when portfolio layer overrules the AI's
                go/no-go decision. Tells the admin "AI says X, but business
                rules say Y because Z" without cluttering when they agree. */}
            {finalIsOverride && (
              <span
                className={`px-2 py-0.5 rounded-full text-[11px] font-medium border flex items-center gap-1 ${FINAL_ACTION_PILL[finalAction] || FINAL_ACTION_PILL.MANUAL_REVIEW}`}
                title={a.booking_priority?.final_action_reason || a.booking_priority?.portfolio_ai_reason || 'Portfolio override'}
              >
                <Sparkles className="w-2.5 h-2.5 shrink-0" strokeWidth={1.5} />
                <span className="text-[10px] uppercase tracking-[0.1em] text-white/40 mr-0.5">Override</span>
                {FINAL_ACTION_LABEL[finalAction] || finalAction}
              </span>
            )}

            {/* Single muted context line — everything you used to read off
                separate pills, now collapsed into one row of small numbers. */}
            <div className="text-[11px] text-stone-500 font-normal flex items-center gap-1.5 flex-wrap sm:justify-end leading-snug">
              <span className={
                a.risk_level === 'critical' ? 'text-rose-300/85'
                : a.risk_level === 'high'   ? 'text-orange-300/85'
                : a.risk_level === 'medium' ? 'text-brand-200/80'
                : 'text-emerald-300/80'
              }>
                Risk <span className="tabular-nums text-stone-300">{a.risk_score}</span>
              </span>
              {a.days_until_departure != null && (
                <>
                  <span className="text-stone-700">·</span>
                  <span className="tabular-nums">{a.days_until_departure}d out</span>
                </>
              )}
              {fareUrg && bookByInfo && (() => {
                const { date, daysFromNow } = bookByInfo;
                const tone =
                  fareUrg === 'critical' ? 'text-rose-300/85'
                  : fareUrg === 'high'   ? 'text-orange-300/85'
                  : fareUrg === 'medium' ? 'text-brand-200/80'
                  : 'text-stone-400';
                let label;
                if (daysFromNow < 0)        label = <>Book <span className="font-medium">ASAP</span> <span className="text-stone-600">(was {format(date, 'MMM d')})</span></>;
                else if (daysFromNow === 0) label = <>Book by <span className="font-medium">today</span></>;
                else                        label = <>Book by <span className="font-medium tabular-nums">{format(date, 'MMM d')}</span> <span className="text-stone-600">(in {daysFromNow}d)</span></>;
                return (
                  <>
                    <span className="text-stone-700">·</span>
                    <span
                      className={tone}
                      title={a.ai_assessment?.fare_trajectory?.reasoning || `Ticket ${fareBy}d before departure (${format(date, 'EEE, MMM d yyyy')})`}
                    >
                      {label}
                    </span>
                  </>
                );
              })()}
            </div>
          </div>
        </div>

        {/* ── Payment progress ────────────────────────────────────────
            Promotes "how much has been paid off so far" from a buried stat
            cell to the row's most prominent visual element. Matches the Royal
            Blue gradient progress strip used by <PaymentScheduleDisplay> on
            the Trips dashboard so admins read the same paid-progress language
            in both places — no click into the Payment Schedule modal needed
            to know whether a booking is 5% in or 95% in. */}
        <PaymentProgressStrip a={a} />

        {/* ── Stats strip ─────────────────────────────────────────────
            With Paid/Remaining absorbed into the progress strip above, this
            row drops from 6 cluttered cells to 4 calmer ones. The boxy INSET
            container is gone in favour of an open row bracketed by hairline
            top/bottom rules — same data, less visual noise. */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-3 mb-2.5 py-2.5 border-y border-white/[0.05]">
          <StatCell label="Departure">
            <span className="flex items-center gap-1.5">
              <Calendar className="w-3 h-3 text-stone-500 shrink-0" strokeWidth={1.25} />
              {fmtDate(a.first_departure_date)}
            </span>
            <sub>{daysLabel(a.days_until_departure)}</sub>
          </StatCell>

          <StatCell label={a.deadline_date ? 'Pay deadline' : 'Deposit'}>
            {a.deadline_date ? (
              <>
                <span className="text-brand-200/85">{fmtDate(a.deadline_date)}</span>
                <sub>{daysLabel(Math.floor((new Date(a.deadline_date) - new Date()) / 86400000))}</sub>
              </>
            ) : (
              <>
                <span>{a.deposit_percentage}%</span>
                <sub>{fmt$(a.upfront_deposit)}</sub>
              </>
            )}
          </StatCell>

          <StatCell label="Expected loss">
            <span className="text-rose-300/85">{fmt$(a.expected_loss)}</span>
            {(a.payment_history?.length > 0) ? (
              <sub>{a.payment_history.length} charge{a.payment_history.length === 1 ? '' : 's'}</sub>
            ) : null}
          </StatCell>

          <StatCell label="Stripe Radar">
            {a.stripe_radar_score != null ? (
              <>
                <span className={`font-mono tabular-nums ${
                  a.stripe_radar_score >= 75 ? 'text-rose-300/90' :
                  a.stripe_radar_score >= 55 ? 'text-orange-300/85' :
                  a.stripe_radar_score >= 35 ? 'text-brand-200/85' :
                  'text-emerald-300/80'
                }`}>
                  {a.stripe_radar_score}
                  <span className="text-stone-500 font-normal">/100</span>
                </span>
                <sub className="capitalize">{a.stripe_radar_level || 'normal'}</sub>
              </>
            ) : (
              <span className="text-stone-600">—</span>
            )}
          </StatCell>
        </div>

        {/* ── Live fare strip ─────────────────────────────────────────
            Real-time SerpAPI price for THIS exact flight (matched on carrier
            + flight number + cabin + passenger breakdown — same matching
            engine the Dashboard's Price Insights uses). Fetched on every
            Refresh+AI and cached for 6h, so this is what the AI verdict
            above was actually reasoning over. Only renders when we have a
            fresh result; absence means SerpAPI didn't return a usable match
            (past departure, missing flight number, etc). */}
        {a.live_fare?.current_price_cents != null && a.live_fare?.booked_base_cents > 0 && (() => {
          const lf = a.live_fare;
          const deltaPct = lf.delta_pct;
          const deltaCents = lf.delta_cents ?? (lf.current_price_cents - lf.booked_base_cents);
          // Tone bands chosen to match the AI prompt's verdict thresholds:
          //   delta >= +10% → fare urgency at least HIGH (rose)
          //   delta <=  -8% → savings opportunity (emerald)
          //   in between    → neutral
          const tone =
            deltaPct >= 10  ? { pill: 'bg-rose-950/45 border-rose-400/25 text-rose-100',          delta: 'text-rose-200',     icon: TrendingUp }
            : deltaPct >= 3 ? { pill: 'bg-orange-950/40 border-orange-400/20 text-orange-100',    delta: 'text-orange-200',   icon: TrendingUp }
            : deltaPct <= -8 ? { pill: 'bg-emerald-950/45 border-emerald-400/25 text-emerald-100', delta: 'text-emerald-200', icon: TrendingDown }
            : deltaPct <= -3 ? { pill: 'bg-emerald-950/35 border-emerald-400/20 text-emerald-100', delta: 'text-emerald-200', icon: TrendingDown }
            : { pill: 'bg-zinc-900/55 border-white/[0.08] text-stone-200', delta: 'text-stone-300', icon: Minus };
          const ToneIcon = tone.icon;
          // Forward-looking forecast (where the fare is headed FROM HERE),
          // computed server-side from Google price insights and carried on the
          // live_fare object. Absent on rows cached before forecasts existed.
          const fc = lf.forecast?.direction ? lf.forecast : null;
          const fcView = fc
            ? (fc.direction === 'up'
                ? { c: 'text-orange-300', Icon: TrendingUp,   label: 'Likely to rise' }
                : fc.direction === 'down'
                  ? { c: 'text-emerald-300', Icon: TrendingDown, label: 'Likely to drop' }
                  : { c: 'text-stone-400', Icon: Minus,         label: 'Likely to hold' })
            : null;
          const FcIcon = fcView?.Icon;
          const fmtCents = (c) => fmt$(Math.round((c ?? 0) / 100));
          const deltaSign = deltaCents >= 0 ? '+' : '−';
          const deltaPctSign = deltaPct >= 0 ? '+' : '−';
          const paxParts = [];
          if (lf.adults)   paxParts.push(`${lf.adults} adult${lf.adults > 1 ? 's' : ''}`);
          if (lf.children) paxParts.push(`${lf.children} child${lf.children > 1 ? 'ren' : ''}`);
          if (lf.infants)  paxParts.push(`${lf.infants} infant${lf.infants > 1 ? 's' : ''}`);
          const paxLabel = paxParts.length > 0 ? paxParts.join(' · ') : `${lf.passenger_count || 1} pax`;
          const cabinLabel = lf.cabin_class ? lf.cabin_class.replace(/_/g, ' ') : 'economy';
          const fetchedAt = lf.fetched_at ? new Date(lf.fetched_at) : null;
          const fetchedAgo = fetchedAt ? (() => {
            const ms = Date.now() - fetchedAt.getTime();
            const m = Math.round(ms / 60000);
            if (m < 1)  return 'just now';
            if (m < 60) return `${m}m ago`;
            const h = Math.round(m / 60);
            if (h < 24) return `${h}h ago`;
            return `${Math.round(h / 24)}d ago`;
          })() : null;
          const tooltip = [
            `Live SerpAPI fare for ${paxLabel} · ${cabinLabel}${lf.matched_exact_flight ? ' · matched exact flight' : ' · cheapest comparable (exact flight not listed)'}`,
            lf.booked_grand_total_cents != null
              ? `Customer was billed ${fmtCents(lf.booked_grand_total_cents)} all-in (incl. service fee). Comparison uses ticket cost only.`
              : null,
            fetchedAgo ? `Fetched ${fetchedAgo}${lf.from_cache ? ' · cached' : ''}` : null,
          ].filter(Boolean).join('\n');
          return (
            <div
              className={`text-xs rounded-lg px-2.5 py-1.5 border font-normal leading-relaxed mb-2 flex items-center justify-between gap-2 flex-wrap ${tone.pill}`}
              title={tooltip}
            >
              <div className="flex items-center gap-2 flex-wrap min-w-0">
                <span className={`${MICRO}`}>Live fare</span>
                <span className="text-stone-400">Booked <span className="tabular-nums text-stone-100">{fmtCents(lf.booked_base_cents)}</span></span>
                <span className="text-stone-600">·</span>
                <span className="text-stone-400">Now <span className="tabular-nums text-stone-100">{fmtCents(lf.current_price_cents)}</span></span>
                {Array.isArray(lf.price_history) && lf.price_history.length >= 2 && (
                  <Sparkline
                    points={lf.price_history}
                    className="w-12 h-4 shrink-0 opacity-90"
                    strokeWidth={1.5}
                  />
                )}
                {lf.booked_grand_total_cents != null && lf.booked_grand_total_cents !== lf.booked_base_cents && (
                  <>
                    <span className="text-stone-600">·</span>
                    <span className="text-stone-500">Grand total <span className="tabular-nums text-stone-300">{fmtCents(lf.booked_grand_total_cents)}</span></span>
                  </>
                )}
                {fcView && (
                  <>
                    <span className="text-stone-600">·</span>
                    <span
                      className={`inline-flex items-center gap-1 font-medium ${fcView.c}`}
                      title={`Near-term fare direction from Google price insights${fc.price_level ? ` — currently ${fc.price_level}` : ''}`}
                    >
                      <FcIcon className="w-3 h-3" strokeWidth={1.5} />
                      {fcView.label}
                    </span>
                  </>
                )}
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <ToneIcon className="w-3.5 h-3.5" strokeWidth={1.5} />
                <span className={`tabular-nums font-medium ${tone.delta}`}>
                  {deltaSign}{fmtCents(Math.abs(deltaCents))}
                </span>
                <span className={`text-[11px] tabular-nums ${tone.delta} opacity-75`}>
                  ({deltaPctSign}{Math.abs(deltaPct).toFixed(1)}%)
                </span>
                {!lf.matched_exact_flight && (
                  <span className="text-[10px] uppercase tracking-[0.1em] text-white/40 ml-1" title="SerpAPI didn't list this exact flight number — comparison is against the cheapest comparable fare in the same cabin">≈</span>
                )}
              </div>
            </div>
          );
        })()}

        {/* ── Rationale ───────────────────────────────────────────── */}
        <div className="text-[13px] text-stone-400 bg-zinc-900/35 rounded-lg px-2.5 py-1.5 border border-white/[0.05] font-normal leading-relaxed mb-2">
          <span className={`${MICRO} mr-2`}>Rationale</span>
          {a.recommendation?.rationale}
        </div>

        {/* ── Drivers & detail ────────────────────────────────────── */}
        {compact && (
          <button
            type="button"
            onClick={() => setShowDeep((v) => !v)}
            aria-expanded={showDeep}
            className="mt-1 w-full flex items-center justify-between gap-2 rounded-lg px-2.5 py-2 text-sm font-normal text-stone-300 bg-zinc-900/40 border border-white/[0.06] hover:text-stone-100 transition-colors"
          >
            <span>Risk drivers &amp; AI analysis</span>
            {showDeep ? <ChevronUp className="w-4 h-4 text-stone-500" /> : <ChevronDown className="w-4 h-4 text-stone-500" />}
          </button>
        )}
        {showDeep && (
          <div className="mt-2 border-t border-white/[0.06] pt-2 space-y-2">
            {/* Holistic AI verdict + fare trajectory (from bulk Refresh+AI batch) */}
            {a.ai_assessment?.ok !== false && (a.ai_assessment?.synthesis || a.ai_assessment?.fare_trajectory) && (
              <div className="space-y-2">
                {a.ai_assessment?.synthesis && (
                  <div className={`${INSET} p-2.5`}>
                    <p className={`${MICRO} mb-1 flex items-center gap-1.5`}>
                      <Brain className="w-3 h-3 text-brand-200/60" strokeWidth={1.5} />
                      AI synthesis · customer + payment + fare
                    </p>
                    <p className="text-stone-200/95 text-sm font-normal leading-[1.6]">{a.ai_assessment.synthesis}</p>
                    {a.ai_assessment.model && (
                      <p className="text-[10px] uppercase tracking-[0.15em] text-stone-600 mt-1.5">{a.ai_assessment.model}</p>
                    )}
                  </div>
                )}
                {a.ai_assessment?.fare_trajectory && (
                  <FareTrajectorySection
                    ft={a.ai_assessment.fare_trajectory}
                    dtd={a.days_until_departure}
                    route={a.route}
                    airline={a.marketing_airline}
                    cabin={a.cabin_class}
                    history={a.live_fare?.price_history}
                  />
                )}
                {a.ai_assessment?.fare_risk_note && (
                  <AiSection label="Fare risk" body={a.ai_assessment.fare_risk_note} />
                )}
              </div>
            )}

            {/* Risk drivers */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
              {(a.risk_drivers || []).map((d, i) => (
                <div key={i} className="flex items-center justify-between text-xs bg-zinc-900/50 rounded-lg px-2.5 py-1.5 border border-white/[0.04] gap-2">
                  <span className="text-stone-300 font-normal min-w-0 truncate">{d.label}</span>
                  <span className={`font-mono shrink-0 ${d.direction === 'down' ? 'text-emerald-300' : 'text-rose-300'}`}>
                    {d.direction === 'down' ? '−' : '+'}
                    {d.weight}
                  </span>
                </div>
              ))}
              {(!a.risk_drivers || a.risk_drivers.length === 0) && (
                <p className="text-stone-600 text-sm font-normal">No drivers flagged.</p>
              )}
            </div>

            {/* Payment history */}
            {Array.isArray(a.payment_history) && a.payment_history.length > 0 && (
              <div>
                <p className={`${MICRO} mb-2 flex items-center gap-2`}>
                  <CreditCard className="w-3.5 h-3.5 text-stone-500" strokeWidth={1.25} />
                  Payment history ({a.payment_history.length})
                </p>
                <div className="space-y-0.5 max-h-36 overflow-y-auto pr-1">
                  {a.payment_history.map((p, i) => (
                    <div key={i} className="grid grid-cols-3 text-xs bg-zinc-900/45 rounded-lg px-2.5 py-1 border border-white/[0.04] gap-2">
                      <span className="text-stone-500 font-normal">{fmtDate(p.date)}</span>
                      <span className={`font-normal text-center ${
                        p.type === 'deposit'  ? 'text-brand-300/75'   :
                        p.type === 'one_time' ? 'text-brand-200/70' : 'text-stone-600'
                      }`}>
                        {p.type === 'deposit' ? 'upfront' : p.type}
                      </span>
                      <span className="text-emerald-300/90 font-mono tabular-nums text-right">{fmt$(p.amount_cents / 100)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* User profile + cart behavior */}
            {(a.user_booking_behavior || a.user_prior_completed != null || a.user_prior_defaulted != null) && (
              <div className={`${INSET} p-2.5`}>
                <p className={`${MICRO} mb-2 flex items-center gap-1.5`}>
                  <UsersIcon className="w-3 h-3 text-stone-500" strokeWidth={1.25} />
                  User profile &amp; behavior
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-2">
                  {/* Booking track record */}
                  <div>
                    <p className={MICRO}>Completed</p>
                    <p className="text-emerald-300/85 text-sm font-mono tabular-nums mt-0.5">
                      {a.user_prior_completed ?? '—'}
                      {a.user_total_prior_bookings != null && (
                        <span className="text-stone-600 font-normal"> / {a.user_total_prior_bookings}</span>
                      )}
                    </p>
                  </div>
                  <div>
                    <p className={MICRO}>Cancelled</p>
                    <p className={`text-sm font-mono tabular-nums mt-0.5 ${(a.user_prior_cancelled ?? 0) > 0 ? 'text-brand-200/80' : 'text-stone-500'}`}>
                      {a.user_prior_cancelled ?? '—'}
                    </p>
                  </div>
                  <div>
                    <p className={MICRO}>Defaulted</p>
                    <p className={`text-sm font-mono tabular-nums mt-0.5 ${(a.user_prior_defaulted ?? 0) > 0 ? 'text-rose-300/90' : 'text-stone-500'}`}>
                      {a.user_prior_defaulted ?? '—'}
                    </p>
                  </div>
                  <div>
                    <p className={MICRO}>Abandoned carts</p>
                    <p className={`text-sm font-mono tabular-nums mt-0.5 ${(a.user_abandoned_cart_count ?? 0) >= 2 ? 'text-brand-200/80' : 'text-stone-400'}`}>
                      {a.user_abandoned_cart_count ?? '—'}
                    </p>
                  </div>
                </div>

                {/* Cart behavior badge + conversion time */}
                {a.user_booking_behavior && a.user_booking_behavior !== 'unknown' && (
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    {(() => {
                      const b = BEHAVIOR_LABEL[a.user_booking_behavior] || BEHAVIOR_LABEL.unknown;
                      return (
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-md border ${b.bg} ${b.color}`}>
                          {b.label}
                        </span>
                      );
                    })()}
                    {a.user_avg_conversion_hours != null && (
                      <span className="text-xs text-stone-500 font-normal">
                        avg {fmtHours(a.user_avg_conversion_hours)} from search to book
                      </span>
                    )}
                    {a.user_cart_abandon_rate != null && (
                      <span className="text-xs text-stone-600 font-normal">
                        · {a.user_cart_abandon_rate}% abandon rate
                      </span>
                    )}
                    {a.user_is_first_time && (
                      <span className="text-xs font-medium text-brand-200/70 bg-brand-950/30 border border-brand-400/15 px-2 py-0.5 rounded-md">
                        First-time user
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Stripe meta */}
            {a.stripe_subscription_id && (
              <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-stone-500 font-normal border-t border-white/[0.05] pt-2.5">
                <span>Sub <span className="font-mono text-stone-400 select-all">{a.stripe_subscription_id}</span></span>
                {a.subscription_status && (
                  <span>Status: <span className={a.subscription_status === 'active' ? 'text-emerald-300' : 'text-brand-300'}>{a.subscription_status}</span></span>
                )}
                {a.per_payment_cents > 0 && (
                  <span>Per charge: <span className="text-stone-400 tabular-nums">{fmt$(a.per_payment_cents / 100)}</span></span>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Per-trip buttons (same modals as the row's icons). On a
            phone they sit above the panel instead (MobileTripButtons). ── */}
        {!compact && (
          <div className="mt-3 pt-2.5 border-t border-white/[0.06] flex items-center justify-end gap-1.5 flex-wrap">
            <Button
              size="sm"
              onClick={onOpenFlight}
              className="rounded-full h-7 px-3 bg-brand-950/40 hover:bg-brand-900/50 text-brand-200/80 border border-brand-400/20 font-normal tracking-wide text-xs"
            >
              <Plane className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.25} />
              Flight details
            </Button>
            <Button
              size="sm"
              onClick={onOpenSchedule}
              className="rounded-full h-7 px-3 bg-emerald-950/40 hover:bg-emerald-900/50 text-emerald-200/80 border border-emerald-400/20 font-normal tracking-wide text-xs"
            >
              <Receipt className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.25} />
              Payment schedule
            </Button>
            <Button
              size="sm"
              onClick={onOpenPrice}
              className="rounded-full h-7 px-3 bg-brand-950/40 hover:bg-brand-900/50 text-brand-200/80 border border-brand-400/20 font-normal tracking-wide text-xs"
            >
              <BarChart2 className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.25} />
              Price insights
            </Button>
            <Button
              size="sm"
              onClick={onOpenHistory}
              className="rounded-full h-7 px-3 bg-stone-900/60 hover:bg-stone-800/70 text-stone-300 border border-white/10 font-normal tracking-wide text-xs"
              title="Read-only audit trail of final_action transitions"
            >
              <ClipboardList className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.25} />
              Decision history
            </Button>
            <Button
              size="sm"
              onClick={onDeepDive}
              className="rounded-full h-7 px-3 bg-brand-950/50 hover:bg-brand-900/50 text-brand-200/95 border border-brand-400/25 font-normal tracking-wide text-xs"
            >
              <Brain className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.25} />
              AI deep dive
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

/** Tiny label + value cell used inside the stats inset */
function StatCell({ label, children }) {
  const [val, sub] = Array.isArray(children) ? children : [children, null];
  return (
    <div className="min-w-0">
      <p className={`${MICRO} mb-1`}>{label}</p>
      <p className="text-stone-100/95 text-sm font-normal leading-tight">{val}</p>
      {sub && <p className="text-stone-500 text-xs font-normal mt-0.5">{sub}</p>}
    </div>
  );
}

/**
 * PaymentProgressStrip — at-a-glance "how much has been paid off" for the row.
 *
 * Mirrors the Royal-Blue gradient progress strip from <PaymentScheduleDisplay>
 * (the one rendered inside the Payment Schedule modal and on the user-facing
 * Trips dashboard) so admins read the same visual language across the app and
 * don't have to click into Payment Schedule just to know whether a booking is
 * 5% in or 95% in. Three rows:
 *
 *   1. eyebrow:  "PAID  $1,420  of $2,898  ·  49%"           [X/Y · Next MMM d]
 *   2. bar:      filled segment (gradient) over a hairline track
 *   3. footnote: "$1,478 remaining · 4 pmts left"     [$125/wk]
 *
 * Gradient is product-aware:
 *   - Defera Capital → Royal Blue (matches DC brand + PaymentScheduleDisplay)
 *   - Defera Zero    → warm Orange (matches the DZ product badge)
 *   - One-time / paid in full → Emerald
 *
 * Renders nothing when we have no usable total amount (degenerate row).
 */
function PaymentProgressStrip({ a }) {
  const isDC = a.payment_method === 'defera_capital';
  const isDZ = a.payment_method === 'defera_zero';

  const totalAmount = Number(a.total_amount) || Number(a.grand_total_with_hold) || 0;
  if (!(totalAmount > 0)) return null;

  const paidAmount = Math.max(0, Number(a.paid_to_date) || 0);
  const remainingAmount = Math.max(
    0,
    Number.isFinite(Number(a.remaining_amount))
      ? Number(a.remaining_amount)
      : totalAmount - paidAmount,
  );
  const paidPct = a.paid_percentage != null
    ? Math.min(100, Math.max(0, Number(a.paid_percentage)))
    : Math.min(100, Math.max(0, (paidAmount / totalAmount) * 100));

  const completedCount = a.payments_completed != null ? Number(a.payments_completed) : null;
  const remainingCount = a.payments_remaining != null ? Number(a.payments_remaining) : null;
  const totalCount = completedCount != null && remainingCount != null
    ? completedCount + remainingCount
    : null;

  // The "deposit floor" provenance means live Stripe is unavailable and the
  // server returned a conservative lower bound. Flag it so admins don't read
  // a low % as truth when it's actually "≥ this much".
  const isFloor   = a.paid_data_source === 'deposit_floor';
  const isCached  = a.paid_data_source === 'db_cache';
  const isStale   = isFloor || isCached;

  const fullyPaid = paidPct >= 99.5;
  const gradient = fullyPaid
    ? 'from-emerald-500 to-emerald-300'
    : isDC
      ? 'from-[hsl(228,67%,12%)] to-[hsl(228,40%,38%)]'
      : isDZ
        ? 'from-orange-600 to-orange-400'
        : 'from-brand-600 to-brand-400';

  const pctTone =
    fullyPaid       ? 'text-emerald-300/90'
    : paidPct >= 50 ? 'text-brand-200/90'
    : paidPct >= 20 ? 'text-stone-300'
    : 'text-stone-400';

  // Right-side context: next charge / one-time deadline / installment ratio.
  const ctxBits = [];
  if (totalCount != null && totalCount > 0) {
    ctxBits.push(`${completedCount}/${totalCount} pmts`);
  }
  if (a.next_payment_date) {
    try { ctxBits.push(`Next ${format(new Date(a.next_payment_date), 'MMM d')}`); }
    catch { /* ignore */ }
  } else if (a.deadline_date && !fullyPaid) {
    try { ctxBits.push(`Due ${format(new Date(a.deadline_date), 'MMM d')}`); }
    catch { /* ignore */ }
  }

  const perPaymentDollars = a.per_payment_cents > 0 ? a.per_payment_cents / 100 : null;
  const freqAbbr = a.payment_frequency === 'monthly' ? 'mo'
    : a.payment_frequency === 'biweekly' ? '2wk'
      : a.payment_frequency === 'weekly' ? 'wk'
        : null;

  return (
    <div className="mb-2.5 rounded-xl border border-white/[0.05] bg-gradient-to-br from-white/[0.025] via-transparent to-transparent px-3 py-2.5">
      {/* Eyebrow row — $paid of $total · % · [right] X/Y · Next date */}
      <div className="flex items-center justify-between gap-3 mb-1.5 flex-wrap">
        <div className="flex items-baseline gap-1.5 min-w-0 flex-wrap">
          <span className={`${MICRO} shrink-0 flex items-center gap-1`}>
            <Wallet className="w-3 h-3 text-brand-200/45" strokeWidth={1.5} />
            Paid
          </span>
          <span className="text-white/95 text-sm font-normal tabular-nums">
            {fmt$(paidAmount)}
            {isFloor && <span className="text-brand-200/60">≥</span>}
          </span>
          <span className="text-stone-500 text-xs font-normal">of {fmt$(totalAmount)}</span>
          <span className={`text-xs font-mono tabular-nums ${pctTone}`}>· {Math.round(paidPct)}%</span>
          {isStale && (
            <span
              className="text-[9.5px] uppercase tracking-[0.14em] text-brand-200/55 font-medium"
              title={isFloor
                ? 'Live Stripe unavailable & no cache — value is a deposit-based lower bound. Hit Refresh to retry.'
                : 'Live Stripe unavailable — showing last-known-good from cache.'}
            >
              {isFloor ? 'floor' : 'cached'}
            </span>
          )}
        </div>
        {ctxBits.length > 0 && (
          <span className="text-[11px] text-stone-400 font-normal tabular-nums shrink-0">
            {ctxBits.join(' · ')}
          </span>
        )}
      </div>

      {/* The bar — 3px Royal Blue gradient over a hairline track. Same visual
          language as PaymentScheduleDisplay's progress strip so admins read
          one consistent paid-progress treatment across the app. */}
      <div className="h-[3px] w-full rounded-full bg-white/[0.06] overflow-hidden">
        <div
          className={`h-full rounded-full bg-gradient-to-r ${gradient} transition-[width] duration-500 ease-out`}
          style={{ width: `${paidPct}%` }}
        />
      </div>

      {/* Footnote row — remaining + installments left  ·  per-charge amount */}
      <div className="mt-1.5 flex items-center justify-between gap-2 text-[10.5px] tabular-nums text-stone-500 font-normal flex-wrap">
        <span>
          {fullyPaid
            ? <span className="text-emerald-300/85">Paid in full</span>
            : <>{fmt$(remainingAmount)} remaining</>}
          {!fullyPaid && remainingCount != null && remainingCount > 0 && (
            <span className="text-stone-600"> · {remainingCount} pmt{remainingCount === 1 ? '' : 's'} left</span>
          )}
        </span>
        {perPaymentDollars != null && freqAbbr && !fullyPaid && (
          <span className="text-stone-500">{fmt$(perPaymentDollars)}/{freqAbbr}</span>
        )}
      </div>
    </div>
  );
}

function DeepDiveBody({ data }) {
  const analysis = data.analysis || {};
  const det      = data.deterministic || {};
  const features = data.features || {};
  const verdict  = analysis.verdict || det.recommendation?.action;
  const VerdictIcon = ACTION_ICON[verdict] || Eye;

  return (
    <div className="space-y-3 pt-1">
      {/* Score pills */}
      <div className="flex flex-wrap items-center gap-2">
        <span className={`px-2.5 py-1 rounded-full text-xs font-medium border flex items-center gap-1.5 ${ACTION_PILL[verdict] || ACTION_PILL.MONITOR}`}>
          <VerdictIcon className="w-3 h-3 shrink-0" strokeWidth={1.25} />
          {ACTION_LABEL_FULL[verdict] || verdict || 'Monitor'}
        </span>
        <span className="text-sm text-stone-500 font-normal">Score <span className="text-stone-200 font-mono tabular-nums">{det.score}</span></span>
        {analysis.confidence != null && (
          <span className="text-sm text-stone-500 font-normal">Confidence <span className="text-stone-200 font-mono tabular-nums">{Math.round((analysis.confidence || 0) * 100)}%</span></span>
        )}
        {analysis.defaulter_match_score != null && (
          <span className="text-sm text-stone-500 font-normal">Match <span className="text-rose-300/90 font-mono tabular-nums">{analysis.defaulter_match_score}</span></span>
        )}
        {features.stripe_radar_score != null && (
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium border flex items-center gap-1 ${radarBadgeStyle(features.stripe_radar_score, features.stripe_radar_level)}`}>
            <Zap className="w-2.5 h-2.5 shrink-0" strokeWidth={1.5} />
            Radar {features.stripe_radar_score} · {features.stripe_radar_level || 'normal'}
          </span>
        )}
      </div>

      {analysis.risk_narrative && (
        <p className="text-stone-300 text-sm leading-[1.65] font-normal">{analysis.risk_narrative}</p>
      )}

      {analysis.customer_profile && <AiSection label="Customer profile" body={analysis.customer_profile} />}
      {analysis.defaulter_match_explanation && <AiSection label="Defaulter comparison" body={analysis.defaulter_match_explanation} />}

      {analysis.fare_trajectory && (
        <FareTrajectorySection
          ft={analysis.fare_trajectory}
          dtd={features.days_until_departure}
          route={features.route}
          airline={features.marketing_airline}
          cabin={features.cabin_class}
          history={features.live_fare?.price_history}
        />
      )}

      {analysis.booking_timing_plan && <AiSection label="Booking timing" body={analysis.booking_timing_plan} />}
      {analysis.fare_risk_note && <AiSection label="Fare risk" body={analysis.fare_risk_note} />}

      {Array.isArray(analysis.escalation_flags) && analysis.escalation_flags.length > 0 && (
        <div>
          <p className={`${MICRO} mb-2`}>Escalation flags</p>
          <ul className="space-y-1">
            {analysis.escalation_flags.map((f, i) => (
              <li key={i} className="flex gap-2 text-sm font-normal text-brand-200/85">
                <span className="text-brand-200/35 shrink-0 select-none">—</span>
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="border-t border-white/[0.08] pt-3">
        <p className={`${MICRO} mb-2`}>Quant signals</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
          {(det.drivers || []).map((d, i) => (
            <div key={i} className="flex items-center justify-between text-xs bg-zinc-900/50 rounded-lg px-2.5 py-1.5 border border-white/[0.04] gap-2">
              <span className="text-stone-400 font-normal min-w-0 truncate">{d.label}</span>
              <span className={`font-mono shrink-0 ${d.direction === 'down' ? 'text-emerald-300' : 'text-rose-300'}`}>
                {d.direction === 'down' ? '−' : '+'}{d.weight}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="border-t border-white/[0.08] pt-3 space-y-2">
        {/* User profile & behavior */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-1.5">
          <div>
            <p className={MICRO}>Completed</p>
            <p className="text-emerald-300/85 text-sm font-mono tabular-nums mt-0.5">
              {data.user_history?.prior_completed ?? 0}
              {data.user_history?.total_prior_bookings != null && (
                <span className="text-stone-600 font-normal"> / {data.user_history.total_prior_bookings}</span>
              )}
            </p>
          </div>
          <div>
            <p className={MICRO}>Cancelled</p>
            <p className={`text-sm font-mono tabular-nums mt-0.5 ${(data.user_history?.prior_cancellations ?? 0) > 0 ? 'text-brand-200/80' : 'text-stone-500'}`}>
              {data.user_history?.prior_cancellations ?? 0}
            </p>
          </div>
          <div>
            <p className={MICRO}>Defaulted</p>
            <p className={`text-sm font-mono tabular-nums mt-0.5 ${(data.user_history?.prior_defaulted ?? 0) > 0 ? 'text-rose-300/90' : 'text-stone-500'}`}>
              {data.user_history?.prior_defaulted ?? 0}
            </p>
          </div>
          <div>
            <p className={MICRO}>Abandoned carts</p>
            <p className={`text-sm font-mono tabular-nums mt-0.5 ${(data.user_history?.abandoned_cart_count ?? 0) >= 2 ? 'text-brand-200/80' : 'text-stone-400'}`}>
              {data.user_history?.abandoned_cart_count ?? '—'}
            </p>
          </div>
        </div>
        {data.user_history?.booking_behavior && data.user_history.booking_behavior !== 'unknown' && (() => {
          const bh = BEHAVIOR_LABEL[data.user_history.booking_behavior] || BEHAVIOR_LABEL.unknown;
          return (
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-xs font-medium px-2 py-0.5 rounded-md border ${bh.bg} ${bh.color}`}>{bh.label}</span>
              {data.user_history.avg_conversion_hours != null && (
                <span className="text-xs text-stone-500 font-normal">
                  avg {fmtHours(data.user_history.avg_conversion_hours)} from search to book
                </span>
              )}
              {data.user_history.cart_abandon_rate != null && (
                <span className="text-xs text-stone-600 font-normal">· {data.user_history.cart_abandon_rate}% abandon rate</span>
              )}
              {data.user_history.is_first_time && (
                <span className="text-xs font-medium text-brand-200/70 bg-brand-950/30 border border-brand-400/15 px-2 py-0.5 rounded-md">First-time user</span>
              )}
            </div>
          );
        })()}
        <p className="text-xs text-stone-600 font-normal">Created {fmtDate(features.created_at)}</p>
      </div>
    </div>
  );
}
